/**
 * Send-time HTML renderer.
 *
 * Takes the template's saved HTML (body text only — no field or listings-block
 * placeholders) plus the structured `template.fields` and
 * `template.listingsBlocks` arrays, and produces the final HTML that gets
 * rendered into the signing PDF. Both fields and listings blocks render as
 * absolutely-positioned `<div>`s anchored on top of the document body — same
 * model the editor uses.
 *
 * Substitution rules (per field):
 *   - Field, signer-fillable        → field tag
 *                                     (`<text-field role="..." />` etc.)
 *   - Field, prefilled w/ source     → resolved value from contact/listing/realtor
 *   - Field, prefilled w/o source    → signer.fieldValues[fieldName] (sender-typed)
 *   - Field, fixed                   → field.defaultValue
 *   - Image-typed prefilled (logo/signature) → <img src="..." />
 *   - Listings block                 → rendered <table> / <ul> with picked listings
 *
 * Pure DOM work (no React) so this module can be called from anywhere in the
 * frontend (NewSignatureForm and the preview dialogs share it).
 */

import {
  SignatureTemplate,
  SignatureTemplateField,
  SignatureDocumentSigner,
  SignaturePrefillSource,
  effectiveFillMode,
  ListingsBlock,
  ListingsBlockColumn,
  AdditionalTermsBlock,
  LISTINGS_BLOCK_COLUMN_WEIGHTS,
  SIGNATURE_FIELD_AFFIX_WIDTH_PX,
  DEFAULT_HIDDEN_ADDRESS_CONSENT_LINES
} from "../@types/signatures";
import { SIGNATURE_EMAIL_VALIDATION_PATTERN } from "../@types/sharedSchema";
import {
  formatPhoneLocal,
  formatPriceIls,
  formatToday,
  formatDate,
  formatListingAddress,
  buildUserInfoBlockText,
  getFieldLabelText,
  getFieldFriendlyName
} from "./signatureFormatters";
import {
  ARIMO_LATIN_WOFF2_BASE64,
  ARIMO_HEBREW_WOFF2_BASE64,
  ARIMO_LATIN_UNICODE_RANGE,
  ARIMO_HEBREW_UNICODE_RANGE
} from "./arimoFontData";
import {
  SIGNATURE_DOCUMENT_WIDTH_PX,
  SIGNATURE_DOCUMENT_PADDING,
  SIGNATURE_DOCUMENT_FONT_FAMILY,
  SIGNATURE_DOCUMENT_FONT_SIZE_PX,
  SIGNATURE_DOCUMENT_LINE_HEIGHT,
  preserveSignatureTrailingLineBreaks
} from "./signatureDocumentLayout";

export type RealtorContext = {
  ownerFullName?: string | null;
  ownerPhoneNumber?: string | null;
  email?: string | null;
  companyName?: string | null;
  companyLogoUrl?: string | null;
  companyId?: string | null;
  signatureImageUrl?: string | null;
  realtorLicense?: string | null;
};

export type ContactContext = {
  contactName?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  idNumber?: string | null;
  additionalPhoneNumbers?: string[] | null;
  /** The contact's own free-text address (contact.address on the card — כתובת הלקוח). */
  address?: string | null;
};

export type ListingContext = {
  /** The address as RENDERED in the document (redacted on hidden-addresses sends). */
  address?: string | null;
  /**
   * The full (unredacted) address. Only differs from `address` on
   * hidden-addresses sends — the addendum builder reads it; the main
   * renderer never does.
   */
  fullAddress?: string | null;
  street?: string | null;
  houseNumber?: string | number | null;
  city?: string | null;
  price?: number | null;
  rooms?: number | null;
  squareMeter?: number | null;
};

export type SendRenderContext = {
  template: SignatureTemplate;
  /** Realtor's profile (the sender). */
  realtor: RealtorContext;
  /** Currently picked contact (signer). One contact per send for now. */
  contact?: ContactContext;
  /** Picked listings. The listings block expands to one row per listing. */
  listings: ListingContext[];
  /** Document number rendered for the `document.formatted_number` source. */
  documentFormattedNumber?: string;
  /** Per-signer values for fields without a prefillSource (sender-typed). */
  signers: SignatureDocumentSigner[];
  /** Maps signer roleId → role display name (used for the field tag's `role` attr). */
  signerRoleNameById: Record<string, string>;
  /**
   * Render mode. `"send"` (default) emits field tags for signer-fillable
   * fields — invisible in a plain browser. `"preview"` emits a labelled,
   * blue-bordered placeholder div instead, so the realtor previewing the
   * document can SEE where signer inputs will go and what they're for.
   * Send-time output should never use preview mode — the signing canvas
   * needs the real tags.
   */
  mode?: "send" | "preview" | "placement";
  /**
   * Per-source value overrides keyed by `field.prefillSource`. When set
   * for a prefilled source, this string REPLACES the value the renderer
   * would have resolved from the realtor / contact / listing context.
   * Used by the send form's "field values" section so the realtor can
   * correct e.g. a mistyped contact name for THIS document only.
   *
   * Keyed by source (not by individual field id) on purpose: a template
   * can reference the same source from many fields (e.g. contact_name +
   * contact_name_signoff) and the realtor wants ONE input that updates
   * all of them — same way the underlying source value would.
   *
   * Has no effect on signer-fillable fields.
   */
  fieldOverrides?: Partial<Record<string, string>>;
  /**
   * Send-time LOCKED prefills of SIGNER-fillable fields, keyed by the field's
   * tag key (`linkGroupId || name`). A non-empty entry demotes the field to
   * prefilled for THIS render: the value is baked into the document (same
   * flow-text treatment as a prefilled field) and NO field tag is emitted —
   * so the signer never gets an input for it, and the server never lists it
   * in `sentFields` (the lock is enforced by construction; a crafted submit
   * for the field name is ignored server-side as an unknown field).
   *
   * EDITABLE seeds deliberately do NOT ride this map — they ride
   * `signers[].fieldValues`, which the public signing payload echoes back as
   * `prefilledValues` to seed the ceremony inputs. Only an explicit realtor
   * "don't let the signer edit this" decision may put a value here.
   */
  lockedSignerValues?: Partial<Record<string, string>>;
  /**
   * Hidden-addresses send. When true, listings blocks whose
   * `allowHiddenAddresses` flag is on ALSO render their consent bullet lines
   * directly below the table. The caller is responsible for having already
   * redacted the addresses in `listings[].address` (the send form builds the
   * listing contexts with the redacted value in this mode).
   */
  hideAddresses?: boolean;
  /**
   * Send-time additional terms typed by the realtor into the send form —
   * rendered into the template's {@link AdditionalTermsBlock} (title
   * underlined above the terms HTML). Ignored when the template has no
   * terms block. Omit (rather than pass empty strings) when the realtor
   * left the section blank — presence is what activates the block.
   */
  additionalTerms?: {
    /** Section title (default "שונות" seeded by the send form). Rendered underlined. */
    title: string;
    /** Rich-text HTML from the send form's mini editor (bold / underline / lists). */
    html: string;
  };
};

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const escapeAttr = (s: string): string => escapeHtml(s);

/**
 * Sanitize a parsed DOM subtree for both clipboard-paste ingress and
 * saved-html render paths.
 *
 * Why: TipTap's editor schema only preserves a narrow set of attributes
 * (TextStyle + our custom fontSize / textAlign / table-cell bg). Most
 * Word- and webpage-pasted HTML carries far more: inline font-family,
 * margin/padding, Office namespace elements (`<o:p>`, `<v:shape>`,
 * `<w:*>`), `<style>` blocks scoped to mso-classnames, trailing `<br>`s
 * before `</p>`, etc. The editor strips or ignores most of it on render,
 * so visually it looks "clean", but two surfaces still see the raw HTML:
 *
 *   1. The preview iframe (`srcDoc` in TemplatePreviewDialog) renders
 *      whatever's in `template.content.html` verbatim.
 *   2. The send-time render path uses the same saved HTML for the
 *      actual signed PDF.
 *
 * If the saved HTML keeps the imported clutter, the editor and the
 * preview show subtly different documents — different fonts, extra
 * vertical space, mis-positioned absolute fields. Cleaning at ingress
 * (paste) and at egress (render) keeps both surfaces in lock-step
 * regardless of what the saved string carries.
 *
 * Removes / normalizes:
 *   - `font-family` (and `font:` shorthand) from any inline `style` attr
 *   - `<font face="...">` elements — children are lifted out
 *   - `<style>` / `<link rel="stylesheet">` blocks (Word's "Web Page,
 *     Filtered" export embeds these — scoped to mso-classnames but a
 *     stray element selector would override our intended layout)
 *   - Inline `margin` / `padding` / `text-indent` / `line-height` on
 *     block elements — Word sets `style="margin:0 0 12pt"` etc. with
 *     pt units that don't match our px-based defaults, producing
 *     paragraphs that render taller/shorter in the iframe than in the
 *     editor (TipTap's schema doesn't preserve these so the editor
 *     uses our defaults, while the iframe respects the inline values
 *     via specificity). Same paragraph margin applied in both surfaces
 *     keeps absolute-field anchors aligned with the text below them.
 *   - Office namespace elements (anything with a `:` in the tag name —
 *     `<o:p>`, `<v:shape>`, `<w:*>`). HTML5 renders unknown tags as
 *     inline by default, so `<o:p>&nbsp;</o:p>` adds an invisible
 *     spacing line in the iframe even though TipTap drops it entirely.
 *   - Presentation-only trailing breaks inserted by ProseMirror. Authored
 *     hard breaks are preserved, including at the end of a paragraph.
 */
export const stripFontCarriersFromDom = (root: Element): void => {
  // Inline style scrubbing — font carriers + Word's block spacing
  // overrides. Done in one pass over `[style]` so we touch each element
  // once. removeProperty silently no-ops if the prop wasn't set, so the
  // call list is the union of "things any imported source might emit".
  root.querySelectorAll<HTMLElement>("[style]").forEach((el) => {
    // `font` is a SHORTHAND: per CSSOM, removing it removes every longhand
    // it covers — including `font-size`, which TipTap's fontSize mark
    // writes as an inline style and the editor honors. Stripping it made
    // toolbar-resized text render at the 14px default in preview/send/PDF
    // while the editor showed 16px — a 3.2px/line vertical drift that
    // accumulated down the page (the tal.abksis "Mac" drift report,
    // 2026-08; the OS was incidental). Capture the size before the
    // shorthand strip and restore it after: the strip exists only to kill
    // Word's `font: ... <family>` carriers, never the size.
    const inlineFontSize = el.style.getPropertyValue("font-size");
    el.style.removeProperty("font-family");
    el.style.removeProperty("font");
    if (inlineFontSize) el.style.setProperty("font-size", inlineFontSize);
    // Block-spacing properties — only meaningful on block elements but
    // CSSStyleDeclaration doesn't care, and inline span/strong/em
    // wouldn't have these set anyway. Stripping uniformly avoids a
    // tag-list maintenance burden.
    el.style.removeProperty("margin");
    el.style.removeProperty("margin-top");
    el.style.removeProperty("margin-bottom");
    el.style.removeProperty("margin-left");
    el.style.removeProperty("margin-right");
    el.style.removeProperty("padding");
    el.style.removeProperty("padding-top");
    el.style.removeProperty("padding-bottom");
    el.style.removeProperty("padding-left");
    el.style.removeProperty("padding-right");
    el.style.removeProperty("text-indent");
    el.style.removeProperty("line-height");
  });

  // Legacy <font face="..."> elements: lift children out and drop wrapper.
  root.querySelectorAll("font").forEach((el) => {
    const parent = el.parentNode;
    if (!parent) return;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  });

  // Word's "Web Page, Filtered" CSS blocks.
  root.querySelectorAll("style, link[rel='stylesheet']").forEach((el) => {
    el.parentNode?.removeChild(el);
  });

  // Office namespace elements — <o:p>, <v:shape>, <w:*>, etc. These
  // can't be selected via querySelector (the `:` confuses the selector
  // parser), so we walk all elements and check tagName. We also drop
  // anything with an `mso-` class as belt-and-suspenders.
  const all = Array.from(root.getElementsByTagName("*"));
  all.forEach((el) => {
    const name = el.tagName.toLowerCase();
    const isNamespaced = name.includes(":");
    const isMsoClass = (el.className || "").toString().toLowerCase().includes("mso");
    if (isNamespaced || isMsoClass) {
      // Lift children rather than discard them — text content survives
      // even if the wrapper is weird Office markup. The wrapper's
      // styles (e.g. `<o:p style="margin:0">`) are already stripped
      // above, so the lifted children land in the parent block.
      const parent = el.parentNode;
      if (!parent) return;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
    }
  });

  // Only editor-generated presentation breaks are disposable. Ordinary
  // trailing <br>s are real Shift+Enter content and must survive rendering.
  root.querySelectorAll("br.ProseMirror-trailingBreak, br[data-webot-trailing-break]").forEach((el) => el.remove());
};

/**
 * ===== Lists: keep the native markup, inject only the markers =====
 *
 * RENDER-ONLY — must never run on the clipboard-paste ingress path
 * (`transformPastedHTML` in TemplateTipTapEditor). The injected spans are
 * presentation for the preview/PDF surfaces; fed back into TipTap they'd
 * parse as literal "5.1." text inside the pasted list items.
 *
 * The render path used to RESTRUCTURE lists (unwrap `<li><p>`, replace
 * whole lists with marker-`<div>`s, collapse empty lists into fixed-height
 * placeholders). All of that existed to emulate the editor's list layout
 * for renderers with list quirks:
 *   - the legacy Playwright print-mode PDF renderer (added its own list
 *     spacing) — REMOVED from the codebase entirely; and
 *   - html2canvas, which draws native `::marker` bullets with LTR-only
 *     logic (bullets land on the wrong side of an RTL document in the
 *     rasterized PDF).
 * Every restructuring pass had to hand-emulate the vertical footprint of
 * the native list it replaced, and each `<li>` shape permutation (blank
 * items, trailing empty paragraphs, nested lists…) was a new drift bug
 * against the editor.
 *
 * Current approach: DON'T restructure. The saved markup renders with the
 * exact structure and CSS the editor uses (`<ol><li><p>` untouched, same
 * paddings/margins), so the text flow is identical to the editor by
 * construction. The only list work left is the marker itself:
 *   - `list-style: none` in the stylesheet removes native markers (which
 *     html2canvas would misplace in RTL), and
 *   - this pass injects one absolutely-positioned `<span>` per `<li>`,
 *     sitting in the list's 24px padding band. Absolute positioning means
 *     it can NEVER affect text flow — marker bugs stay visual, never
 *     become drift.
 *
 * Numbering is HIERARCHICAL: a nested ordered item renders "2.1." rather
 * than restarting at "1." — `prefix` carries the parent chain down the
 * recursion. This mirrors the editor's CSS-counter markers
 * (`counters(webot-ol, ".")` in TemplateTipTapEditor.tsx — keep the two
 * in sync; the renderer uses real spans instead of ::before+counters only
 * because html2canvas support for pseudo-element counters is unreliable).
 * The ol `start` attribute (TipTap emits it on split lists) offsets the
 * first number, matching the editor's ol[start] counter-reset rules.
 * `prefix` passes THROUGH unordered levels unchanged, mirroring CSS
 * counter scoping (a <ul> neither increments nor resets the counter).
 */
export const injectListMarkersIntoDom = (root: Element): void => {
  const injectListMarkers = (list: Element, prefix: string): void => {
    const isOrdered = list.tagName === "OL";
    const startAttr = parseInt(list.getAttribute("start") || "1", 10);
    const start = Number.isFinite(startAttr) && startAttr > 0 ? startAttr : 1;
    const items = Array.from(list.children).filter((c) => c.tagName === "LI") as HTMLElement[];
    const doc = list.ownerDocument!;
    items.forEach((li, idx) => {
      const itemNumber = `${prefix}${start + idx}.`;
      const markerSpan = doc.createElement("span");
      // inset-inline-end:100% pins the marker's text-adjacent edge to the
      // li's inline-start edge (right in RTL, left in LTR) inside the list's
      // padding band; auto width + nowrap let a long marker ("2.10.",
      // "2.1.3.") grow OUTWARD into the padding / page margin instead of
      // overlapping the item text (a fixed centered 24px band spilled onto
      // the text once sub-numbers hit two digits). margin-inline-end keeps a
      // small constant gap to the text. Being absolutely positioned also
      // keeps the digits bidi-safe (an inline marker's Latin digits get
      // shuffled by the bidi algorithm when surrounded by Hebrew). Mirror of
      // the editor's ::before marker CSS in TemplateTipTapEditor.tsx.
      markerSpan.setAttribute("data-list-marker", "true");
      markerSpan.setAttribute(
        "style",
        "position:absolute;inset-inline-end:100%;margin-inline-end:6px;white-space:nowrap;"
      );
      markerSpan.textContent = isOrdered ? itemNumber : "•";
      // position:relative so the marker anchors to this li (the stylesheet's
      // `li { position: relative }` covers it too — inline is belt-and-braces
      // for any consumer that drops the stylesheet).
      const liStyle = li.getAttribute("style") || "";
      li.setAttribute("style", `${liStyle ? `${liStyle.replace(/;$/, "")};` : ""}position:relative;`);
      li.insertBefore(markerSpan, li.firstChild);
      Array.from(li.children).forEach((c) => {
        if (c.tagName === "OL" || c.tagName === "UL") {
          // Ordered parents extend the chain ("2." → "2.1."); unordered
          // parents pass it through untouched (CSS counter scoping).
          injectListMarkers(c, isOrdered ? itemNumber : prefix);
        }
      });
    });
  };

  // Walk only top-level lists; nested ones get their markers from the
  // recursion. A list is "top-level" when no ancestor up to the root is an
  // LI (i.e., it's not nested inside another list item).
  const isTopLevelList = (list: Element): boolean => {
    let parent: Element | null = list.parentElement;
    while (parent && parent !== root) {
      if (parent.tagName === "LI") return false;
      parent = parent.parentElement;
    }
    return true;
  };
  Array.from(root.querySelectorAll("ol, ul"))
    .filter(isTopLevelList)
    .forEach((list) => injectListMarkers(list, ""));
};

const COLUMN_HEADERS: Record<ListingsBlockColumn, string> = {
  address: "כתובת",
  price: "מחיר",
  rooms: "חדרים",
  square_meter: 'מ"ר'
};

const formatListingValue = (l: ListingContext, col: ListingsBlockColumn): string => {
  switch (col) {
    case "address":
      return (
        l.address ||
        formatListingAddress({ street: l.street, houseNumber: l.houseNumber, city: l.city })
      );
    case "price":
      return l.price != null ? formatPriceIls(l.price) : "";
    case "rooms":
      return l.rooms != null ? String(l.rooms) : "";
    case "square_meter":
      return l.squareMeter != null ? String(l.squareMeter) : "";
    default:
      return "";
  }
};

/**
 * Resolve a single prefilled field's value from the surrounding render
 * context. Returns an empty string for missing data so the document still
 * renders cleanly (the realtor sees a blank where data is missing rather
 * than a broken substitution).
 */
const resolvePrefilledValue = (field: SignatureTemplateField, ctx: SendRenderContext): string => {
  const src: SignaturePrefillSource | undefined = field.prefillSource;
  if (!src || src === "none") return field.defaultValue || "";
  switch (src) {
    case "today":
      return formatToday(field.dateFormat);
    case "contact.name":
      return ctx.contact?.contactName || "";
    case "contact.phone":
      return ctx.contact?.phoneNumber ? formatPhoneLocal(ctx.contact.phoneNumber) : "";
    case "contact.email":
      return ctx.contact?.email || "";
    case "contact.id_number":
      return ctx.contact?.idNumber || "";
    case "contact.additional_phones":
      // Multiple numbers render as a comma-separated list, each in local form.
      return (ctx.contact?.additionalPhoneNumbers || [])
        .filter(Boolean)
        .map((p) => formatPhoneLocal(p))
        .join(", ");
    case "signing_date":
      // The signing date is stamped by us at completion (readonly date
      // field) — never baked in here. Returning "" keeps prefilled renders blank.
      return "";
    case "contact.address":
      return ctx.contact?.address || "";
    case "listing.address":
      // The listings block is the multi-row primitive; a single field uses
      // the first listing's address.
      return (
        ctx.listings[0]?.address ||
        formatListingAddress(
          ctx.listings[0]
            ? {
                street: ctx.listings[0].street,
                houseNumber: ctx.listings[0].houseNumber,
                city: ctx.listings[0].city
              }
            : null
        )
      );
    case "listing.price":
      return ctx.listings[0]?.price != null ? formatPriceIls(ctx.listings[0]!.price!) : "";
    case "listing.rooms":
      return ctx.listings[0]?.rooms != null ? String(ctx.listings[0].rooms) : "";
    case "listing.square_meter":
      return ctx.listings[0]?.squareMeter != null ? String(ctx.listings[0].squareMeter) : "";
    case "realtor.name":
      return ctx.realtor.ownerFullName || "";
    case "realtor.phone":
      return ctx.realtor.ownerPhoneNumber ? formatPhoneLocal(ctx.realtor.ownerPhoneNumber) : "";
    case "realtor.email":
      return ctx.realtor.email || "";
    case "realtor.company":
      return ctx.realtor.companyName || "";
    case "realtor.license":
      return ctx.realtor.realtorLicense || "";
    case "realtor.company_id":
      return ctx.realtor.companyId || "";
    case "realtor.user_info_block":
      return buildUserInfoBlockText(
        {
          ownerFullName: ctx.realtor.ownerFullName,
          ownerPhoneNumber: ctx.realtor.ownerPhoneNumber,
          realtorLicense: ctx.realtor.realtorLicense,
          companyId: ctx.realtor.companyId,
          companyName: ctx.realtor.companyName
        },
        field.userInfoBlockConfig
      );
    case "document.formatted_number":
      return ctx.documentFormattedNumber || "";
    default:
      return field.defaultValue || "";
  }
};

/**
 * Image-typed prefill sources resolve to a URL rather than a text value.
 * Returns null when the source isn't an image (caller falls back to text).
 */
const resolvePrefilledImageUrl = (
  field: SignatureTemplateField,
  ctx: SendRenderContext
): string | null => {
  if (field.type !== "image") return null;
  if (field.prefillSource === "realtor.company_logo") return ctx.realtor.companyLogoUrl || null;
  if (field.prefillSource === "realtor.signature_image")
    return ctx.realtor.signatureImageUrl || null;
  return null;
};

const SIGNER_TAG_BY_TYPE: Record<SignatureTemplateField["type"], string> = {
  signature: "signature-field",
  initials: "initials-field",
  text: "text-field",
  date: "date-field",
  checkbox: "checkbox-field",
  number: "number-field",
  // 'percentage' is a number constrained to 0-100. There is no native
  // percentage tag — we render as <number-field> (digits-only input) and
  // stamp the "%" suffix beside it via the affix wrapper, so the signed PDF
  // shows "75%" with a real numeric input behind it.
  percentage: "number-field",
  // 'price' (dynamic price) is sender-typed in practice — the value ("1.5%"
  // or "₪1,500,000") is baked at send time. If a template ever flips it to
  // signer-fillable it degrades to a free-text input.
  price: "text-field",
  // Radio fields are sender-only and therefore normally never reach the
  // signer branch. Keep a safe text fallback for malformed legacy data.
  radio: "text-field",
  image: "image-field",
  // 'phone' is a phone-shaped text field. We render it as a <text-field>
  // with PHONE_VALIDATION_PATTERN attached so the signer-side UI rejects
  // malformed entries. SMS-OTP verification is enforced at the signer level
  // (see signatureOtp.service.ts), not per-field.
  phone: "text-field",
  // There is no dedicated email-field tag — email validation is enforced
  // by attaching a `pattern` attribute to a regular text-field (see
  // EMAIL_VALIDATION_PATTERN below). The signer-side UI then rejects input
  // that doesn't match.
  email: "text-field"
};

/**
 * HTML5 email validation regex used as the `pattern` attribute on the
 * <text-field> when our field type is "email". Matches anything with a
 * non-whitespace local part, an `@`, a non-whitespace domain part, a dot,
 * and a non-whitespace TLD — the signing canvas rejects input that doesn't
 * match this shape before allowing submission. Shared with the server, which
 * recognises a sent email field by it (`isSignatureEmailField`: an email field
 * is never required of the signer), so it is never a local copy.
 */
const EMAIL_VALIDATION_PATTERN = SIGNATURE_EMAIL_VALIDATION_PATTERN;

/**
 * HTML5 phone validation regex used as the `pattern` attribute on the
 * <text-field> when our field type is "phone" (the no-OTP variant
 * — see SIGNER_TAG_BY_TYPE comment above). Permissive on purpose:
 * accepts digits with optional leading "+", spaces, dashes, and parens,
 * 6-20 chars total. We deliberately don't enforce a specific country
 * format here — the realtor-side validateFieldValue() does the strict
 * libphonenumber check before sending; this pattern is just a signer-side
 * shape gate so the canvas rejects obviously-wrong entries (e.g. letters).
 */
const PHONE_VALIDATION_PATTERN = "^[+]?[0-9 \\-()]{6,20}$";

/**
 * Map our internal SignatureDateFormat to the `format` attribute value we
 * emit on date-field tags. The renderer accepts the same DD/MM/YYYY-style
 * tokens we use, except for our "long" (Hebrew written) format — there is
 * no natively-supported long Hebrew format, so we omit the attribute and
 * fall back to the default short format. The realtor's "long" choice still
 * affects PREFILLED date fields (those go through formatToday and stamp text
 * directly) — the loss is only on signer-fillable date fields, which would
 * have to type a long-form Hebrew date by hand anyway.
 */
const SUPPORTED_DATE_FORMATS = new Set([
  "DD/MM/YYYY",
  "MM/DD/YYYY",
  "YYYY-MM-DD",
  "DD-MM-YYYY",
  "DD.MM.YYYY"
]);

/**
 * Resolve which prefix / suffix text to render beside a field at send time
 * (and in the editor's preview). Centralised so prefilled and signer-fillable
 * branches stay consistent — both pull the same source-of-truth:
 *   - prefix: realtor's `labelText` if `showLabel` is on, falling back to the
 *     hard-coded default for the field's source (`getFieldLabelText`) for
 *     legacy fields written before `labelText` was editable.
 *   - suffix: `suffix` when `showSuffix` is on. No prefill-source fallback —
 *     the realtor types this from scratch (or it's seeded at field-creation
 *     time for percentage, see `DEFAULT_FIELD_SUFFIX_BY_TYPE`).
 *
 * Returns empty strings (not undefined) so callers can do straightforward
 * truthiness checks without optional-chaining.
 */
const getEffectiveAffixes = (
  field: SignatureTemplateField
): {
  prefixText: string;
  suffixText: string;
} => {
  const prefixText = field.showLabel
    ? field.labelText || getFieldLabelText(field.labelKey || field.prefillSource) || ""
    : "";
  const suffixText = field.showSuffix ? field.suffix || "" : "";
  return { prefixText, suffixText };
};

/**
 * Build the HTML field tag for a signer-fillable field.
 *
 * Style: the tag fills the absolutely-positioned wrapper exactly, via
 * `position:absolute; inset:0`. The signing canvas renders its OWN visual
 * placeholder (a fillable box / underline) for the tag — that's the visible
 * cue the signer sees, and it's also what the canvas focuses / scrolls to
 * when the signer activates the field. We deliberately don't render a
 * competing visible placeholder div on top, and we don't hide the tag with
 * `opacity:0` — either of those breaks the canvas's native cue.
 *
 * Attributes the realtor configured in the editor are emitted on the tag so
 * the stamped value matches what they designed:
 *   - align — horizontal alignment of the typed value. Default "right"
 *     (vs the tag vocabulary's own default "left") because every Hebrew
 *     document is RTL by default; an unspecified field would otherwise stamp
 *     left-aligned and look broken next to right-aligned body text.
 *   - font-size — pixel font size of the stamped value, matching the
 *     editor preview. Without this the canvas autosizes from the field
 *     height, which often diverges from what the realtor saw.
 *   - format (date fields) — date display format on the signed PDF.
 *   - required — gates submission on signer side. (Already passed.)
 *
 * Native field types provide their own input validation in the signer
 * UI: number-field rejects non-numeric input, date-field uses a date
 * picker, phone-field validates phone format. There's no "email" tag
 * in the tag vocabulary — email validation is done by rendering it as
 * <text-field pattern="..." /> (see EMAIL_VALIDATION_PATTERN).
 *
 * When a prefix and/or suffix is configured, the tag's `left:` / `right:`
 * insets are pushed in by the affix sliver width (estimated per-label by
 * `computeAffixLayout` so multi-character Hebrew labels aren't clipped) so
 * the affix spans (rendered by `renderSignerAffixSpans`) sit beside the
 * input rather than overlapping it. Prefix position depends on align
 * (right / center → RTL flow → prefix on right; left → LTR flow → prefix
 * on left). Suffix is ALWAYS on the right edge because the common case is
 * the "%" suffix on a percentage field, where the typed value (right-aligned
 * by default in our Hebrew docs) needs to abut the suffix on its right.
 */
/**
 * Estimate the rendered pixel width of an affix string at a given font size.
 * Used to size the prefix/suffix sliver (and the matching field-input
 * inset) so the label isn't clipped by a fixed 24px box when the realtor
 * picks a multi-character Hebrew label like "מחיר" or "תאריך חתימה".
 *
 * Pure heuristic: avg glyph width ≈ 0.62 × font-size for mixed Hebrew/Latin
 * bold text, plus 4px of horizontal padding on each side so the text breathes
 * away from both the sliver edge and the field input. Floored at
 * SIGNATURE_FIELD_AFFIX_WIDTH_PX (the realtor's existing single-glyph
 * baseline) and capped at 240px so a runaway label can't eat the whole input.
 */
const FIELD_AFFIX_HORIZONTAL_PADDING_PX = 4;
const FIELD_AFFIX_MAX_WIDTH_PX = 240;
const measureAffixTextPx = (text: string, fontSize: number): number => {
  if (!text) return 0;
  const estimate = Math.ceil(text.length * fontSize * 0.62);
  const withPadding = estimate + FIELD_AFFIX_HORIZONTAL_PADDING_PX * 2;
  return Math.min(Math.max(withPadding, SIGNATURE_FIELD_AFFIX_WIDTH_PX), FIELD_AFFIX_MAX_WIDTH_PX);
};

/**
 * Resolve the affix layout for a signer-fillable field: how wide each sliver
 * needs to be, and how much the field tag must be inset on each side to
 * leave room for them. Called from both `renderSignerAffixSpans` and
 * `buildSignerFieldTag` so the two stay in lockstep — any drift between
 * the sliver width and the input inset would either clip the label or leave
 * a transparent gap where the field input shows through.
 */
const computeAffixLayout = (
  field: SignatureTemplateField
): {
  prefixWidthPx: number;
  suffixWidthPx: number;
  leftInsetPx: number;
  rightInsetPx: number;
  prefixOnRight: boolean;
} => {
  const { prefixText, suffixText } = getEffectiveAffixes(field);
  const fontSize = field.fontSize || 14;
  const prefixWidthPx = measureAffixTextPx(prefixText, fontSize);
  const suffixWidthPx = measureAffixTextPx(suffixText, fontSize);
  const align = field.align || "right";
  const prefixOnRight = align !== "left";
  const leftInsetPx = !prefixOnRight ? prefixWidthPx : 0;
  // When prefix lives on the right too (the RTL default), stack the prefix
  // beside the suffix on the same edge — they get distinct widths now, so
  // the old "both at right:0 → overlap" case is also fixed.
  const rightInsetPx = (prefixOnRight ? prefixWidthPx : 0) + suffixWidthPx;
  return { prefixWidthPx, suffixWidthPx, leftInsetPx, rightInsetPx, prefixOnRight };
};

const buildSignerFieldTag = (field: SignatureTemplateField, roleName: string): string => {
  const tag = SIGNER_TAG_BY_TYPE[field.type] || "text-field";
  const attrs: string[] = [];
  // Our renderer groups multiple field tags that share the same `name`
  // attribute into ONE input on the signing page — fill once, populate
  // everywhere. We use linkGroupId for that grouping when present
  // (set by the editor's "duplicate field" flow and by drops from the
  // contact / listing / realtor / document tile categories) so signer-
  // fillable duplicates are auto-linked. Falls back to the field's
  // unique name otherwise.
  const signerFieldName = field.linkGroupId || field.name;
  attrs.push(`name="${escapeAttr(signerFieldName)}"`);
  attrs.push(`role="${escapeAttr(roleName)}"`);
  // A field tag defaults to required=true when the attribute is absent — so we
  // only need to emit it explicitly when the field is optional. Without this,
  // unchecking "Required" in our editor had no effect on the signer-side UI
  // and the canvas still blocked submission on empty values.
  if (!field.required) attrs.push(`required="false"`);
  if (field.signerLabel) attrs.push(`title="${escapeAttr(field.signerLabel)}"`);
  if (field.signerDescription) attrs.push(`description="${escapeAttr(field.signerDescription)}"`);

  // Horizontal alignment of the stamped value. The tag accepts
  // left/center/right; our schema uses the same vocabulary so we pass
  // through directly. Default "right" matches the rest of the renderer
  // (Hebrew RTL document body).
  const align = field.align || "right";
  attrs.push(`align="${escapeAttr(align)}"`);

  // Stamped value font size in px. Skip when unset so the canvas's
  // height-based autosize stays in effect.
  if (field.fontSize) {
    attrs.push(`font-size="${field.fontSize}"`);
  }

  // Date format applies only to date-field tags and only for formats
  // the renderer natively supports. See SUPPORTED_DATE_FORMATS comment above.
  if (field.type === "date" && field.dateFormat && SUPPORTED_DATE_FORMATS.has(field.dateFormat)) {
    attrs.push(`format="${escapeAttr(field.dateFormat)}"`);
  }

  // Email / phone validation. The field tag exposes input validation
  // through the `pattern` attribute (HTML5 pattern semantics); attaching a
  // regex makes the signing canvas reject anything that isn't a well-formed
  // value before allowing submission. SMS-OTP verification is enforced
  // at the signer level (see signatureOtp.service.ts), not per-field.
  if (field.type === "email") {
    attrs.push(`pattern="${escapeAttr(EMAIL_VALIDATION_PATTERN)}"`);
  } else if (field.type === "phone") {
    attrs.push(`pattern="${escapeAttr(PHONE_VALIDATION_PATTERN)}"`);
  }

  // The HTML `pattern` attribute above is NOT read by our PDF extraction
  // (signaturesHtmlToPdf) — so on its own it wouldn't survive into the
  // extracted field list. Emit `data-*` markers that extraction DOES read,
  // so the server can apply them as per-signer validation rules the signing
  // canvas enforces at submit time.
  if (field.type === "email") {
    attrs.push(`data-validation-pattern="${escapeAttr(EMAIL_VALIDATION_PATTERN)}"`);
  }
  // Signing-date field: a readonly date field we auto-stamp with the
  // signing date at completion. The marker → readonly per-signer override.
  if (field.prefillSource === "signing_date") {
    attrs.push(`data-readonly="true"`);
  }

  // Prefix sits where the reading flow STARTS: right edge in RTL (default
  // for our Hebrew docs), left edge in LTR. Suffix sits on the right edge
  // unconditionally — see the buildSignerFieldTag doc comment for why.
  // Sliver widths are estimated from the label/suffix text in
  // `computeAffixLayout` so multi-character Hebrew labels aren't clipped.
  const { leftInsetPx, rightInsetPx } = computeAffixLayout(field);
  const style =
    `display:inline-block;position:absolute;left:${leftInsetPx}px;top:0;right:${rightInsetPx}px;bottom:0;` +
    "box-sizing:border-box;";
  attrs.push(`style="${style}"`);
  return `<${tag} ${attrs.join(" ")}></${tag}>`;
};

/**
 * Render the prefix / suffix spans that sit beside a signer-fillable input.
 *
 * The field tag occupies the wrapper's interior with explicit
 * `left:` / `right:` insets matching the affix widths (see
 * `buildSignerFieldTag`). These spans fill the leftover slivers on each
 * side — pure visual stamps that render straight into the PDF.
 *
 * Position rules mirror `buildSignerFieldTag`:
 *   - prefix: right edge in RTL flow (align != 'left'), left edge in LTR.
 *   - suffix: always on the right edge.
 *
 * Returns the two HTML fragments separately so the caller (in the send
 * pipeline) can decide how to order them inside the wrapper.
 */
const renderSignerAffixSpans = (
  field: SignatureTemplateField
): { prefixHtml: string; suffixHtml: string } => {
  const { prefixText, suffixText } = getEffectiveAffixes(field);
  if (!prefixText && !suffixText) return { prefixHtml: "", suffixHtml: "" };
  const { prefixWidthPx, suffixWidthPx, prefixOnRight } = computeAffixLayout(field);
  const fontSize = field.fontSize ? `font-size:${field.fontSize}px;` : "";
  const color = field.color ? `color:${field.color};` : "";
  // The span needs to render on top of the input area only if the field
  // rect were ever to overlap — but we've already shrunk the tag's insets,
  // so the spans sit in their own slivers and a high z-index
  // is just defensive. `pointer-events:none` so a click on the affix area
  // still focuses the input behind it.
  //
  // Vertical alignment: `line-height: ${hPx}px` would be the cleanest
  // single-line centering, but we don't have hPx here — fall back to a flex
  // center with `line-height: 1.3` (matching wrapInnerBlock) so the bold
  // label's line box doesn't inherit the document body's 1.6 line-height
  // and visibly drop below the input baseline the canvas centers on.
  const baseStyle =
    `position:absolute;top:0;bottom:0;` +
    `display:flex;align-items:center;justify-content:center;` +
    `pointer-events:none;font-weight:600;line-height:1.3;` +
    `padding:0 ${FIELD_AFFIX_HORIZONTAL_PADDING_PX}px;box-sizing:border-box;` +
    `white-space:nowrap;overflow:hidden;text-overflow:ellipsis;` +
    `${fontSize}${color}`;
  const prefixHtml = prefixText
    ? `<span style="${escapeAttr(
        baseStyle +
          `width:${prefixWidthPx}px;` +
          // When suffix also lives on the right edge, the prefix sits to its
          // LEFT (i.e. `right: suffixWidthPx`). Otherwise it hugs the chosen
          // edge directly.
          (prefixOnRight ? `right:${suffixWidthPx}px;` : "left:0;")
      )}">${escapeHtml(prefixText)}</span>`
    : "";
  const suffixHtml = suffixText
    ? `<span style="${escapeAttr(baseStyle + `width:${suffixWidthPx}px;right:0;`)}">${escapeHtml(
        suffixText
      )}</span>`
    : "";
  return { prefixHtml, suffixHtml };
};

/**
 * Wrap a prefilled value's HTML with the realtor's chosen prefix label
 * (`showLabel + labelText`) and/or suffix (`showSuffix + suffix`). Used for
 * flow-text rendering — values that aren't position:absolute (prefilled /
 * fixed / sender-typed) — so prefix and suffix can sit inline with the
 * value and inherit the wrapping container's text-align.
 *
 * Suffix is rendered as a plain inline `<span>` (no bold) so "%" reads as
 * part of the value, not as a heading; prefix stays in `<strong>` to match
 * the long-standing "caption: value" look on contact-prefixed fields.
 */
const wrapWithLabel = (innerHtml: string, field: SignatureTemplateField): string => {
  const { prefixText, suffixText } = getEffectiveAffixes(field);
  if (!prefixText && !suffixText) return innerHtml;
  const prefix = prefixText ? `<strong>${escapeHtml(prefixText)}</strong> ` : "";
  // `margin-inline-start:6px` opens a small gap between the typed value and
  // the appended suffix so "75%" doesn't read as one collated glyph clump.
  // Logical-property margin so the gap lands on the correct side in both
  // RTL (default) and LTR documents.
  const suffix = suffixText
    ? `<span style="margin-inline-start:6px;">${escapeHtml(suffixText)}</span>`
    : "";
  return `${prefix}${innerHtml}${suffix}`;
};

/**
 * Wrap a field's inner content in a block-level div sized to fill the
 * absolute wrapper, with text-align baked in. Mirrors the editor's
 * AbsoluteFieldOverlay structure (an inner block div is what makes
 * `text-align` reliably apply — text-align on an absolutely-positioned
 * wrapper is honoured by browsers but the print-mode PDF rendering
 * has been observed to drop it, which is why field alignment looked fine
 * in the in-form preview but was off in the generated PDF).
 */
const wrapInnerBlock = (innerHtml: string, field: SignatureTemplateField): string => {
  const styles = [
    "width:100%",
    "height:100%",
    "box-sizing:border-box",
    `text-align:${field.align || "right"}`,
    "line-height:1.3",
    field.prefillSource === "realtor.user_info_block" ? "white-space:pre-line" : ""
  ]
    .filter(Boolean)
    .join(";");
  return `<div style="${styles}">${innerHtml}</div>`;
};

/**
 * Caption for a signer-fillable field in PREVIEW mode. Order of preference:
 * the realtor's custom signer-side label → the prefill friendly name →
 * the field's internal name as a last resort. Keeps the preview informative
 * without leaking placeholders that won't appear on the final document.
 */
const previewSignerCaption = (field: SignatureTemplateField): string => {
  if (field.signerLabel) return field.signerLabel;
  const friendly = getFieldFriendlyName(field.prefillSource || field.labelKey);
  return friendly || field.name;
};

/**
 * Render a signer-fillable field as a visible placeholder for the preview
 * surface. Blue dashed border + the field's caption — matches what the
 * realtor sees in the editor overlay so the preview doesn't feel like a
 * different document.
 */
const renderSignerPreviewPlaceholder = (field: SignatureTemplateField): string => {
  const caption = previewSignerCaption(field);
  const icon =
    field.type === "signature"
      ? "✍️ "
      : field.type === "initials"
      ? "🅰 "
      : field.type === "checkbox"
      ? "☐ "
      : field.type === "image"
      ? "🖼 "
      : "";
  const style =
    "display:flex;align-items:center;justify-content:center;height:100%;width:100%;" +
    "border:1.5px dashed #1976D2;background:rgba(25,118,210,0.06);color:#0D47A1;" +
    "font-size:12px;font-weight:600;border-radius:4px;padding:2px 6px;" +
    "box-sizing:border-box;text-align:center;";
  return `<div style="${escapeAttr(style)}">${escapeHtml(icon + caption)}</div>`;
};

/**
 * Build the inline `<span>` markup for a typed/resolved value, accounting
 * for type-specific rendering (checkbox glyph, date format, phone local
 * format). Returns `null` when there's nothing to render — checkbox is the
 * exception: it always renders a glyph, with empty/missing meaning unchecked.
 *
 * Centralising this keeps the override / fixed / sender-typed / prefilled
 * branches consistent — without it a checkbox value showed up in the document
 * as the literal string "true", and a sender-typed date showed up in the
 * raw "YYYY-MM-DD" form regardless of the realtor's chosen dateFormat.
 */
const renderInlineValueHtml = (
  field: SignatureTemplateField,
  value: string | undefined | null,
  styleAttr: string
): string | null => {
  if (field.type === "checkbox") {
    const checked = value === "true";
    return `<span${styleAttr}>${checked ? "☑" : "☐"}</span>`;
  }
  if (!value) return null;
  if (field.type === "date") {
    const formatted = formatDate(value, field.dateFormat);
    if (!formatted) return null;
    return `<span${styleAttr}>${escapeHtml(formatted)}</span>`;
  }
  if (field.type === "phone") {
    return `<span${styleAttr}>${escapeHtml(formatPhoneLocal(value))}</span>`;
  }
  if (field.type === "radio") {
    const option = field.radioFieldConfig?.options.find((candidate) => candidate.value === value);
    return option ? `<span${styleAttr}>${escapeHtml(option.label)}</span>` : null;
  }
  return `<span${styleAttr}>${escapeHtml(value)}</span>`;
};

/**
 * Replace one field placeholder span with its resolved substitution.
 *
 * Exported so the in-editor preview can reuse the SAME field-resolution
 * logic when rendering the read-only TipTap-based preview. Reusing the
 * function (rather than re-implementing the branching in JSX) keeps the
 * preview and the actual send pipeline producing the same value for a
 * given field — anything else would be a footgun where the realtor's
 * preview lies about what the customer will receive.
 */
export const renderFieldReplacement = (
  field: SignatureTemplateField,
  ctx: SendRenderContext
): string => {
  if (ctx.mode === "placement") return "";
  const mode = effectiveFillMode(field);
  const fontSize = field.fontSize ? `font-size:${field.fontSize}px;` : "";
  const color = field.color ? `color:${field.color};` : "";
  // Inline span carries font-size/color only — text-align lives on the
  // outer block wrapper (`wrapInnerBlock`) where it actually applies.
  const styleAttr = fontSize || color ? ` style="${escapeAttr(`${fontSize}${color}`)}"` : "";

  if (mode === "signer") {
    // A LOCKED send-time prefill demotes the field to prefilled for this
    // render: bake the value like any prefilled field (inline affixes via
    // wrapWithLabel) and emit no tag. Applies to the preview too — the
    // preview must show exactly what the signer will receive.
    const lockedValue = (ctx.lockedSignerValues?.[field.linkGroupId || field.name] ?? "").trim();
    if (lockedValue) {
      const lockedInner = renderInlineValueHtml(field, lockedValue, styleAttr);
      if (lockedInner) return wrapInnerBlock(wrapWithLabel(lockedInner, field), field);
    }
    // Preview surface gets a labelled placeholder; send surface gets the
    // field tag the signing canvas will replace with a real input. Affix
    // spans (prefix / suffix) sit alongside, anchored to the parent
    // absolute wrapper (PreviewFieldOverlay / the send pipeline both
    // mount the field inside a position:absolute container — so these
    // spans' absolute positioning resolves against THAT container).
    const { prefixHtml, suffixHtml } = renderSignerAffixSpans(field);
    if (ctx.mode === "preview") {
      return `${prefixHtml}${renderSignerPreviewPlaceholder(field)}${suffixHtml}`;
    }
    const roleName = ctx.signerRoleNameById[field.signerRoleId] || "Signer";
    return `${prefixHtml}${buildSignerFieldTag(field, roleName)}${suffixHtml}`;
  }

  // Per-source override (set by the send form's "field values" panel)
  // wins over context resolution. Keyed by prefillSource so a source used
  // by many fields shares one override. Image-typed sources are skipped
  // here — they fall through to the image branch below.
  if (field.prefillSource && field.type !== "image") {
    const override = ctx.fieldOverrides?.[field.prefillSource];
    // Checkbox lets "" mean "explicitly unchecked", so an override of ""
    // is still a real override; for every other type, "" means "no
    // override typed, fall through to the resolver".
    const hasOverride = override !== undefined && (field.type === "checkbox" || override !== "");
    if (hasOverride) {
      const inner = renderInlineValueHtml(field, override, styleAttr);
      if (inner) {
        return wrapInnerBlock(wrapWithLabel(inner, field), field);
      }
    }
  }

  // image-typed prefilled: fill the designed wrapper, matching the editor.
  // Hard-coding max-height:64px made every uploaded logo render smaller
  // than the realtor's designed box (which is sized in the editor with
  // resize handles); object-fit:contain keeps aspect while filling.
  const imageUrl = resolvePrefilledImageUrl(field, ctx);
  if (imageUrl) {
    return `<img src="${escapeAttr(imageUrl)}" alt="${escapeAttr(
      field.name
    )}" style="display:block;width:100%;height:100%;object-fit:contain;" />`;
  }

  if (mode === "fixed") {
    const text = field.defaultValue || "";
    const inner = renderInlineValueHtml(field, text, styleAttr);
    if (!inner) return "";
    return wrapInnerBlock(wrapWithLabel(inner, field), field);
  }

  // 'prefilled' with a source — resolve it.
  if (field.prefillSource && field.prefillSource !== "none") {
    let value = resolvePrefilledValue(field, ctx);
    // Phone gets a final pass in case the source returned an E.164 string
    // (resolvePrefilledValue already formats the realtor/contact phone
    // sources, so this is a belt-and-suspenders normalisation).
    //
    // Date is intentionally NOT re-formatted here. The only date-typed
    // prefill source is `today`, and resolvePrefilledValue already calls
    // formatToday(field.dateFormat) for it — re-parsing the formatted
    // string via new Date() loses the user's chosen format (Hebrew "long"
    // becomes Invalid Date and renders blank; slash formats get day/month
    // swapped by V8's MM/DD/YYYY-default parser).
    if (field.type === "phone" && value) value = formatPhoneLocal(value);
    if (!value) return "";
    // user_info_block contains \n line breaks — convert to <br>.
    const html =
      field.prefillSource === "realtor.user_info_block"
        ? value
            .split("\n")
            .map((line) => escapeHtml(line))
            .join("<br>")
        : escapeHtml(value);
    return wrapInnerBlock(wrapWithLabel(`<span${styleAttr}>${html}</span>`, field), field);
  }

  // 'prefilled' with NO source — sender typed the value into NewSignatureForm.
  //
  // Lookup key honours linkGroupId so a duplicated sender-typed field
  // ("text_1" with linkGroupId="text_1") and its copy ("text_1_2", same
  // linkGroupId) both pick up the SAME typed value. NewSignatureForm
  // stores typed values keyed by linkGroupId-or-name; the fallback to
  // `field.name` covers any pre-link legacy data.
  //
  // With a single-signer flow this is just the first signer's value; with
  // multiple signers the realtor is expected to give consistent values,
  // so we use the first non-empty.
  const senderLookupKey = field.linkGroupId || field.name;
  // Checkbox is ALWAYS rendered (empty/missing → ☐, "true" → ☑); for every
  // other type, only a non-empty value produces output, matching the prior
  // "no value, render nothing" behaviour.
  const senderValue = ctx.signers
    .map((s) => s.fieldValues?.[senderLookupKey] ?? s.fieldValues?.[field.name])
    .find((v) => (field.type === "checkbox" ? v !== undefined : !!v && v.length > 0));
  const inner = renderInlineValueHtml(field, senderValue ?? "", styleAttr);
  if (!inner) return "";
  return wrapInnerBlock(wrapWithLabel(inner, field), field);
};

/**
 * Compute concrete CSS-pixel column widths from the block's overrides + the
 * weighted defaults — same algorithm the editor overlay uses, kept in sync so
 * the rendered table matches what the realtor saw. Always normalises the
 * result to sum to the block width exactly (see overlay equivalent for the
 * "hidden-column-with-overrides" case that motivated this).
 */
const computeColumnWidthsPx = (block: ListingsBlock): Record<ListingsBlockColumn, number> => {
  const cols = block.columns?.length
    ? block.columns
    : (["address", "price", "rooms", "square_meter"] as ListingsBlockColumn[]);
  const totalW = block.position?.wPx ?? 400;
  const overrides = block.columnWidthsPx || {};
  const fixed = cols.reduce((acc, c) => acc + (overrides[c] || 0), 0);
  const flexCols = cols.filter((c) => !overrides[c]);
  const remaining = Math.max(0, totalW - fixed);
  const flexWeight = flexCols.reduce((acc, c) => acc + (LISTINGS_BLOCK_COLUMN_WEIGHTS[c] ?? 1), 0);
  const out = {} as Record<ListingsBlockColumn, number>;
  for (const c of cols) {
    if (overrides[c]) {
      out[c] = overrides[c]!;
    } else if (flexCols.length > 0) {
      const weight = LISTINGS_BLOCK_COLUMN_WEIGHTS[c] ?? 1;
      out[c] = flexWeight > 0 ? (remaining * weight) / flexWeight : remaining / flexCols.length;
    } else {
      out[c] = totalW / cols.length;
    }
  }
  const sum = cols.reduce((acc, c) => acc + (out[c] || 0), 0);
  if (sum > 0 && Math.abs(sum - totalW) > 0.5) {
    const scale = totalW / sum;
    for (const c of cols) out[c] = (out[c] || 0) * scale;
  }
  return out;
};

/**
 * Rough vertical-pixel estimate of how tall a listings block will render
 * once expanded for a given listings count. Used to size the document
 * body so a tall table doesn't fall off the bottom of `.webot-doc`.
 *
 * Values are conservative — better to over-allocate a bit of empty
 * space at the bottom of the page than to clip rows.
 */
const estimateListingsBlockHeightPx = (
  block: ListingsBlock,
  listingsCount: number,
  ctx?: Pick<SendRenderContext, "hideAddresses">
): number => {
  const fontSize = block.fontSize ?? 14;
  // Table cell padding is 6px top + 6px bottom in the renderer; line
  // height ≈ fontSize * 1.4. Round up generously so wrapped addresses
  // don't push us past the estimate.
  const rowHeight = Math.ceil(fontSize * 1.4 + 14);
  // Static safety buffer added to every block's estimated height. Real-
  // world tables came in fractionally taller than the estimator
  // predicted (sub-pixel rounding from the browser's table layout +
  // border/padding stacking that's hard to model exactly). A small fixed
  // pad here gets the body text reliably below the rendered table edge
  // — cheaper than tightening every per-row estimate and there's no
  // visual cost beyond a thin gap.
  const SAFETY_BUFFER_PX = 50;
  // Hidden-addresses sends append the consent bullet lines below the table.
  // The default lines run ~120-160 chars each — at ~14px in a typical block
  // width they wrap to 2-3 lines, so estimate 3 rendered lines per bullet.
  // The runtime shift script measures the REAL height anyway; this estimate
  // only sizes the overall document so nothing clips before that runs.
  const consentLines =
    ctx?.hideAddresses && block.allowHiddenAddresses
      ? (block.hiddenAddressConsentLines ?? DEFAULT_HIDDEN_ADDRESS_CONSENT_LINES).filter((l) =>
          l.trim()
        ).length
      : 0;
  const consentHeight = consentLines > 0 ? consentLines * Math.ceil(fontSize * 1.4) * 3 + 12 : 0;
  if (block.format === "bulleted-list") {
    // Bulleted list rows are tighter (no padding cells), but allow some
    // breathing room for wrapping.
    return Math.max(
      block.position.hPx,
      listingsCount * rowHeight + 8 + consentHeight + SAFETY_BUFFER_PX
    );
  }
  // Header row + body rows. Add 4px to absorb sub-pixel rounding plus
  // the safety buffer above so downstream content clears the table.
  const headerHeight = rowHeight;
  return Math.max(
    block.position.hPx,
    headerHeight + listingsCount * rowHeight + 4 + consentHeight + SAFETY_BUFFER_PX
  );
};

/**
 * Render a listings block as the inner HTML for an absolutely-positioned
 * wrapper div. Exported alongside `renderFieldReplacement` so the in-editor
 * preview overlay can use the same listing-row formatting as the send path.
 */
export const renderListingsBlockInnerHtml = (
  block: ListingsBlock,
  ctx: Pick<SendRenderContext, "listings" | "hideAddresses">
): string => {
  const cols = block.columns?.length
    ? block.columns
    : (["address", "price", "rooms", "square_meter"] as ListingsBlockColumn[]);
  const fontStyle = `font-size:${block.fontSize ?? 14}px;${
    block.color ? `color:${block.color};` : ""
  }`;
  // Hidden-addresses consent bullets — rendered directly below the table /
  // list on hidden-addresses sends so the signer's agreement to "address
  // disclosed after signature" is part of the same visual block.
  const consentHtml =
    ctx.hideAddresses && block.allowHiddenAddresses
      ? renderHiddenAddressConsentHtml(block, fontStyle)
      : "";
  if (block.format === "bulleted-list") {
    // Marker-div technique instead of a native <ul> — see
    // renderBulletLinesHtml for why (html2canvas draws ::marker bullets
    // LTR-only, putting them on the LEFT of an RTL document in the PDF).
    const items = ctx.listings.map((l) => cols.map((c) => formatListingValue(l, c)).join(" — "));
    return `${renderBulletLinesHtml(items, `${fontStyle}margin:0;`)}${consentHtml}`;
  }
  const isBordered = block.format === "table-bordered";
  const widths = computeColumnWidthsPx(block);
  const colgroup = `<colgroup>${cols
    .map((c) => `<col style="width:${widths[c]}px" />`)
    .join("")}</colgroup>`;
  // Cell padding stays at `4px 6px` to match the editor overlay
  // (AbsoluteListingsBlockOverlay) — realtors design block heights
  // assuming this row size. Wider padding makes each row taller and
  // fewer rows fit in the designed hPx than the editor preview
  // suggested, a visible regression.
  const headerRow = cols
    .map(
      (c) =>
        `<th style="padding:4px 6px;text-align:start;${
          isBordered ? "border:1px solid #ccc;background:#f7f7f7;" : ""
        }">${escapeHtml(COLUMN_HEADERS[c])}</th>`
    )
    .join("");
  const bodyRows = ctx.listings
    .map(
      (l) =>
        `<tr>${cols
          .map(
            (c) =>
              `<td style="padding:4px 6px;text-align:start;${
                isBordered ? "border:1px solid #ccc;" : ""
              }">${escapeHtml(formatListingValue(l, c))}</td>`
          )
          .join("")}</tr>`
    )
    .join("");
  return `<table style="table-layout:fixed;border-collapse:collapse;width:100%;${fontStyle}">${colgroup}<thead><tr>${headerRow}</tr></thead><tbody>${bodyRows}</tbody></table>${consentHtml}`;
};

/**
 * Bulleted lines WITHOUT a native <ul>/<li> — each line is a `<div>` with an
 * absolutely-positioned "•" span at its inline-start edge, mirroring the
 * `injectListMarkers` technique used on body-content lists. Two reasons a
 * native marker can't be used on the send path:
 *   - html2canvas (the PDF rasterizer) draws `::marker` bullets with its own
 *     LTR-only logic, so on our RTL documents the PDF showed the bullets on
 *     the LEFT while the live iframe showed them on the right.
 *   - the marker span sits at a browser-computed absolute position, which
 *     html2canvas rasterizes faithfully; `inset-inline-start:0` resolves to
 *     the right edge under the document's `dir="rtl"`.
 */
const renderBulletLinesHtml = (lines: string[], wrapperStyle: string): string => {
  const items = lines
    .map(
      (l) =>
        `<div style="position:relative;padding-inline-start:22px;margin-bottom:4px;">` +
        `<span style="position:absolute;inset-inline-start:0;width:22px;text-align:center;">•</span>` +
        `${escapeHtml(l)}</div>`
    )
    .join("");
  return `<div style="${wrapperStyle}line-height:1.4;">${items}</div>`;
};

/**
 * Consent bullet lines rendered below a listings table on hidden-addresses
 * sends. Exported alongside `renderListingsBlockInnerHtml` so the editor's
 * overlay preview can show the realtor the same lines they're editing in the
 * block settings sheet.
 */
export const renderHiddenAddressConsentHtml = (block: ListingsBlock, fontStyle: string): string => {
  const lines = (block.hiddenAddressConsentLines ?? DEFAULT_HIDDEN_ADDRESS_CONSENT_LINES)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return "";
  return renderBulletLinesHtml(lines, `${fontStyle}margin:8px 0 0;`);
};

// ============================================================================
// Additional-terms block (send-time free-form terms)
// ============================================================================

/**
 * Sanitize the send form's rich-text terms HTML for the render surfaces.
 * The content comes from our own TipTap mini editor (bold / underline /
 * lists), but it goes through the same hygiene passes as the template body:
 *   - stripFontCarriersFromDom — parity with pasted-content cleanup.
 *   - active-content removal — belt-and-braces; TipTap never emits these.
 *   - injectListMarkersIntoDom — the page stylesheet disables native list
 *     markers (html2canvas draws ::marker LTR-only), so terms lists need the
 *     same injected marker spans the body lists get.
 */
const sanitizeAdditionalTermsHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(
    `<!doctype html><html><body>${html}</body></html>`,
    "text/html"
  );
  doc.body.querySelectorAll("script, iframe, object, embed").forEach((el) => el.remove());
  stripFontCarriersFromDom(doc.body);
  preserveSignatureTrailingLineBreaks(doc.body);
  injectListMarkersIntoDom(doc.body);
  return doc.body.innerHTML;
};

/**
 * Breathing room appended below a FILLED terms section. The runtime shift
 * pass pushes downstream content by the block's measured `offsetHeight`, so
 * this must be PADDING (margins don't count toward offsetHeight) — it rides
 * the measurement and becomes part of the push, keeping whatever sits under
 * the anchor (typically the signature section) from hugging the last terms
 * line. Empty sections render nothing, so the gap costs nothing when unused.
 */
const ADDITIONAL_TERMS_BOTTOM_GAP_PX = 32;

/**
 * Render the additional-terms block's inner HTML: the section title
 * (underlined, bold) above the realtor's terms content. The wrapper carries
 * the block's font size / colour; paragraph and list styling comes from the
 * page stylesheet so the terms read exactly like body text.
 */
const renderAdditionalTermsInnerHtml = (
  block: AdditionalTermsBlock,
  terms: NonNullable<SendRenderContext["additionalTerms"]>
): string => {
  const fontStyle = `font-size:${block.fontSize ?? 14}px;${
    block.color ? `color:${block.color};` : ""
  }`;
  const title = terms.title.trim();
  const titleHtml = title
    ? `<div style="font-weight:600;text-decoration:underline;margin-bottom:6px;">${escapeHtml(
        title
      )}</div>`
    : "";
  return `<div style="${fontStyle}padding-bottom:${ADDITIONAL_TERMS_BOTTOM_GAP_PX}px;">${titleHtml}${sanitizeAdditionalTermsHtml(
    terms.html
  )}</div>`;
};

/**
 * Conservative vertical-pixel estimate of the rendered terms block — same
 * role as {@link estimateListingsBlockHeightPx}: it only sizes the overall
 * document (`.webot-doc` min-height) so nothing clips before the runtime
 * shift script measures the REAL height and applies the actual push. Lines
 * are estimated per block-level element from text length vs. block width;
 * over-allocating a little empty page bottom is fine, clipping is not.
 */
const estimateAdditionalTermsHeightPx = (
  block: AdditionalTermsBlock,
  terms: NonNullable<SendRenderContext["additionalTerms"]>
): number => {
  const fontSize = block.fontSize ?? 14;
  const lineHeight = Math.ceil(fontSize * 1.6);
  // ~0.55 × font-size average glyph width for mixed Hebrew/Latin text (same
  // heuristic family as measureAffixTextPx); floor keeps a degenerate narrow
  // block from exploding the line count.
  const charsPerLine = Math.max(10, Math.floor(block.position.wPx / (fontSize * 0.55)));
  const doc = new DOMParser().parseFromString(
    `<!doctype html><html><body>${terms.html}</body></html>`,
    "text/html"
  );
  const blockEls = doc.body.querySelectorAll("p, li, h1, h2, h3, h4, h5, h6");
  let lines = 0;
  if (blockEls.length === 0) {
    lines = Math.max(1, Math.ceil((doc.body.textContent || "").length / charsPerLine));
  } else {
    blockEls.forEach((el) => {
      lines += Math.max(1, Math.ceil((el.textContent || "").trim().length / charsPerLine));
    });
  }
  const titleLines = terms.title.trim() ? 1 : 0;
  const SAFETY_BUFFER_PX = 40;
  return Math.max(
    block.position.hPx,
    (lines + titleLines) * lineHeight + ADDITIONAL_TERMS_BOTTOM_GAP_PX + SAFETY_BUFFER_PX
  );
};

/**
 * Walk the saved template HTML and produce the final HTML to send.
 *
 * Fields and listings blocks are NOT inline in the saved HTML — they live on
 * `template.fields` and `template.listingsBlocks` respectively, with absolute
 * (x, y, w, h) positions, and are emitted as positioned `<div>`s over the
 * document body. The HTML body itself contains only the template's body
 * text (paragraphs, headings, list items, etc.).
 *
 * Uses the browser's DOMParser — pure-frontend module.
 */
export const renderHtmlForSend = (savedHtml: string, ctx: SendRenderContext): string => {
  const parser = new DOMParser();
  const dom = parser.parseFromString(
    `<!doctype html><html><body>${savedHtml}</body></html>`,
    "text/html"
  );
  const body = dom.body;

  // Strip font carriers from the saved HTML — see stripFontCarriersFromDom
  // for the rationale. Same operation applied to clipboard HTML at paste
  // time (see TemplateTipTapEditor's `transformPastedHTML`).
  stripFontCarriersFromDom(body);
  preserveSignatureTrailingLineBreaks(body);

  // Inject list marker spans — RENDER-ONLY (paste ingress must never do
  // this; the spans would parse back into TipTap as literal marker text).
  injectListMarkersIntoDom(body);

  // ===== Compute reflow shifts for absolute content below listings tables =====
  // The realtor designs each listings block at a fixed (x, y, w, h). At send
  // time we don't know how many listings they'll pick — fewer than designed
  // is fine (table renders shorter than the designed box), MORE than designed
  // means the table grows downward past its designed bottom. Anything else
  // anchored below that bottom (other listings blocks, signature/text fields)
  // would otherwise stay at its designed Y and be visually overlapped by the
  // overflowing rows.
  //
  // Fix: compute per-block overflow (estimated natural height minus designed
  // height) and shift every absolutely-positioned thing whose Y sits at or
  // below a block's designed bottom by the sum of overflows from all blocks
  // above it. Block ordering is purely Y-based; horizontal position doesn't
  // factor into "below" because the page is column-narrow.
  const blocks = ctx.template.listingsBlocks || [];
  // The additional-terms block is active only when the realtor actually
  // typed terms for this send — an empty section renders nothing (the block
  // stays at its designed footprint in the layout, no title, no push).
  const activeTermsBlock =
    ctx.template.additionalTermsBlock && ctx.additionalTerms
      ? ctx.template.additionalTermsBlock
      : null;

  // Every absolute block that can GROW past its designed height at send time
  // — listings tables (grow with the picked listings) and the terms block
  // (grows with the typed terms) — with a conservative estimate of its
  // natural (expanded) height. The runtime shift script re-measures the REAL
  // heights; these estimates only size the overall document so nothing clips
  // before it runs.
  const expandableBlocks: { yPx: number; hPx: number; naturalPx: number }[] = [
    ...blocks.map((b) => ({
      yPx: b.position.yPx,
      hPx: b.position.hPx,
      naturalPx: estimateListingsBlockHeightPx(b, ctx.listings.length, ctx)
    })),
    ...(activeTermsBlock && ctx.additionalTerms
      ? [
          {
            yPx: activeTermsBlock.position.yPx,
            hPx: activeTermsBlock.position.hPx,
            naturalPx: estimateAdditionalTermsHeightPx(activeTermsBlock, ctx.additionalTerms)
          }
        ]
      : [])
  ];
  const shiftForY = (yPx: number): number => {
    let shift = 0;
    expandableBlocks.forEach((b) => {
      const designedBottom = b.yPx + b.hPx;
      // Anything starting at or below the designed bottom of an earlier
      // block gets shifted by that block's overflow. Strictly-greater
      // would let a tightly-packed sibling overlap when the block grows.
      if (yPx >= designedBottom) shift += Math.max(0, b.naturalPx - b.hPx);
    });
    return shift;
  };

  // ===== Push body-flow text down past overflowing tables =====
  // Listings blocks are absolutely positioned overlays — they sit ON TOP
  // of the body text rather than displacing it. When a block overflows
  // its designed height, the new rows cover whatever paragraph happened
  // to be in that vertical band. Realtors expect the text to reflow
  // around the table the way a Word document does.
  //
  // The push is applied at RUNTIME inside the rendered iframe (see the
  // inline `applyBodyPushFromMeasurements` script below) rather than
  // here at HTML-build time. The previous approach estimated each body
  // element's height from text length and tag and walked them in order
  // to find the first paragraph past each overflowing block's designed
  // bottom — but the estimate diverged from reality whenever the body
  // contained anything the heuristic didn't model exactly (Word-imported
  // `<br>`-heavy paragraphs, tables, images, nested wrappers, mixed
  // Hebrew/Latin runs). The mismatch fired the push on the wrong
  // paragraph: body content sitting right past the block stayed put
  // while fields shifted by the full overflow, producing the "fields
  // pushed more than text" misalignment.
  //
  // Runtime measurement reads each paragraph's actual `offsetTop` (which
  // is relative to `.webot-doc` thanks to `.webot-body`'s lack of
  // positioning), finds the first paragraph whose real top is past the
  // block's designed bottom, and applies the same overflow value used
  // for field shifts — no estimate involved. See the inline script
  // toward the end of the HTML template.

  // Render every listings block as an absolutely-positioned <div>. The
  // designed height is treated as a MINIMUM only (no overflow clipping)
  // so the table grows naturally when more listings get picked.
  //
  // We render each block at its DESIGNED `top` with NO shift baked in —
  // the inline `applyShiftsFromMeasurements` script below applies all
  // shifts at runtime from real DOM measurements. Trying to compute
  // overflow at HTML-build time means estimating row heights and adding
  // a static safety buffer to absorb the inaccuracy, and that buffer
  // ends up applied to fields and body push slightly asymmetrically
  // whenever real layout differs from the estimate — the "fields go a
  // few extra pixels lower than text" symptom. Doing all shifts from
  // the same real `offsetHeight` measurement removes the asymmetry.
  //
  // Data attrs the runtime script reads: the designed Y origin and
  // designed (min-)height. designedBottom is reconstructed in JS.
  const absoluteBlocksHtml = blocks
    .map((block) => {
      const pos = block.position;
      const styleParts = [
        "position:absolute",
        `left:${pos.xPx}px`,
        `top:${pos.yPx}px`,
        `width:${pos.wPx}px`,
        `min-height:${pos.hPx}px`,
        "box-sizing:border-box"
      ];
      return `<div data-block-y-designed="${pos.yPx}" data-block-h-designed="${
        pos.hPx
      }" style="${styleParts.join(";")}">${renderListingsBlockInnerHtml(block, ctx)}</div>`;
    })
    .join("");

  // The additional-terms block joins the same absolute-overlay layer with
  // the SAME data attributes, so the runtime shift script treats it exactly
  // like a listings table: grow past the designed height → push every
  // field, block, and body paragraph below it down by the measured overflow.
  // Rendering and alignment stay separate concerns — this emits content at
  // its designed spot; the shift script owns making room for it.
  const termsBlockHtml =
    activeTermsBlock && ctx.additionalTerms
      ? (() => {
          const pos = activeTermsBlock.position;
          const styleParts = [
            "position:absolute",
            `left:${pos.xPx}px`,
            `top:${pos.yPx}px`,
            `width:${pos.wPx}px`,
            `min-height:${pos.hPx}px`,
            "box-sizing:border-box"
          ];
          return `<div data-block-y-designed="${pos.yPx}" data-block-h-designed="${
            pos.hPx
          }" style="${styleParts.join(";")}">${renderAdditionalTermsInnerHtml(
            activeTermsBlock,
            ctx.additionalTerms
          )}</div>`;
        })()
      : "";

  // Render every field as a positioned <div> over the document body. Fields
  // without a position (legacy data) get clamped to the top-left so they're
  // still visible and editable rather than silently lost. Direction mirrors
  // alignment: a left-aligned field implies LTR content (e.g. an English
  // number / email), so we set dir=ltr to keep punctuation in the right
  // visual order — matches the editor preview.
  //
  // Order fields by visual position (top-to-bottom, then right-to-left for
  // our Hebrew/RTL docs) before emitting tags. The signing canvas infers
  // signer tab order from tag order in the HTML, so chronological add-order —
  // what we used to emit — produced confusing signing flows when the realtor
  // laid out fields out of order.
  const sortedFieldsForSend = [...ctx.template.fields].sort((a, b) => {
    const ay = a.position?.yPx ?? 32;
    const by = b.position?.yPx ?? 32;
    if (ay !== by) return ay - by;
    const ax = a.position?.xPx ?? 32;
    const bx = b.position?.xPx ?? 32;
    return bx - ax;
  });
  const absoluteFieldsHtml = sortedFieldsForSend
    .map((f) => {
      const pos = f.position || { xPx: 32, yPx: 32, wPx: 200, hPx: 28 };
      const mode = effectiveFillMode(f);
      const dir = f.align === "left" ? "ltr" : "rtl";
      // Image fields drop the 2/4px padding so the rendered <img> fills the
      // designed box edge-to-edge — same call the editor's overlay makes.
      // Without this, every uploaded logo renders 4px narrower / 4px shorter
      // than the realtor sized it.
      const isImageField = f.type === "image";
      // Field top is the DESIGNED yPx — no shift baked in here. The
      // `applyShiftsFromMeasurements` script below applies the real
      // overflow-derived shift at runtime, using the SAME measurement
      // it uses to compute body-paragraph push, so fields and body
      // move by exactly the same amount.
      const styleParts = [
        "position:absolute",
        `left:${pos.xPx}px`,
        `top:${pos.yPx}px`,
        `width:${pos.wPx}px`,
        `height:${pos.hPx}px`,
        f.fontSize ? `font-size:${f.fontSize}px` : "",
        f.color ? `color:${f.color}` : "",
        `text-align:${f.align || "right"}`,
        `direction:${dir}`,
        "box-sizing:border-box",
        isImageField ? "" : "padding:2px 4px",
        "overflow:hidden",
        // Match the editor overlay's inner content `lineHeight: 1.3` so
        // single-line fields don't sit at a different vertical position
        // (body's 1.6 line-height is too generous for a 28px-tall field box
        // and visibly drops text down by ~2-3px compared with the editor).
        isImageField ? "" : "line-height:1.3",
        // Match the editor overlay's `wordBreak: break-word` so long values
        // wrap at the same character in both surfaces.
        isImageField ? "" : "word-wrap:break-word",
        f.prefillSource === "realtor.user_info_block" ? "white-space:pre-line" : ""
      ].filter(Boolean);

      // Marker attribute so the runtime shift pass can find this field
      // and adjust its `top` based on real block measurements.
      const dataAttr = ` data-field-y-designed="${pos.yPx}"`;

      // Invisible geometry participates in the compiler's safe page cuts.
      // The editor paints its interactive fields using the returned page map.
      if (ctx.mode === "placement") return `<div${dataAttr} style="${styleParts.join(";")}"></div>`;

      // A locked send-time prefill renders through the prefilled path below
      // (baked value, standard padding, inline affixes) instead of emitting
      // a signer tag — `renderFieldReplacement` resolves the locked value.
      const hasLockedValue =
        mode === "signer" && !!(ctx.lockedSignerValues?.[f.linkGroupId || f.name] ?? "").trim();
      if (mode === "signer" && !hasLockedValue) {
        // Drop the wrapper padding so the field's box matches its
        // designed edges precisely.
        const wrapperStyle = styleParts.filter((s) => s !== "padding:2px 4px").join(";");
        // Affix spans (prefix / suffix) sit inside the same wrapper as the
        // field tag, anchored to opposite slivers. Returned even when
        // empty — `renderSignerAffixSpans` already short-circuits to "" so
        // the no-affix case emits zero extra DOM.
        const { prefixHtml, suffixHtml } = renderSignerAffixSpans(f);
        if (ctx.mode === "preview") {
          // Browser preview: render our blue dashed-border placeholder
          // since the signing canvas hasn't mounted yet — this is the
          // realtor's "what will the signer see roughly here" cue.
          return `<div${dataAttr} style="${wrapperStyle}">${prefixHtml}${renderSignerPreviewPlaceholder(
            f
          )}${suffixHtml}</div>`;
        }
        // Send: emit just the field tag inside the absolute wrapper.
        // The signing canvas renders its native placeholder (the fillable
        // box) at the tag's rect; that's the visible cue on the document
        // AND the target the canvas focuses when the signer activates the
        // field. Stacking our own placeholder on top — or hiding the tag
        // with opacity:0 — broke both.
        const roleName = ctx.signerRoleNameById[f.signerRoleId] || "Signer";
        return `<div${dataAttr} style="${wrapperStyle}">${prefixHtml}${buildSignerFieldTag(
          f,
          roleName
        )}${suffixHtml}</div>`;
      }
      const inner = renderFieldReplacement(f, ctx);
      return `<div${dataAttr} style="${styleParts.join(";")}">${inner}</div>`;
    })
    .join("");

  // ===== Compute the document's required height =====
  // `.webot-doc` is `position: relative` — its absolutely-positioned
  // children (fields + listings blocks) DON'T contribute to its natural
  // height. So a 5-listing table that grows to 400px past the bottom of
  // the body's flow content would simply spill off the page.
  //
  // To keep everything contained, we compute the lowest pixel any
  // absolute child will occupy (using a conservative estimate for
  // listings tables based on the picked listings count) and force
  // `.webot-doc` to be at least that tall plus the bottom padding.
  // The canonical compiler measures the final bounds and captures full pages;
  // the PDF viewer uses those same page boxes in preview and signing.
  const A4_PAGE_HEIGHT_PX = 1123;
  const DOC_BOTTOM_PADDING_PX = 48;
  // Bottoms must use the SHIFTED Y (otherwise the doc box ends where the
  // designed positions said it should, but the rendered fields/table sit
  // below that and fall off the page).
  const fieldBottoms = ctx.template.fields.map((f) => {
    const pos = f.position || { yPx: 32, hPx: 28 };
    return pos.yPx + shiftForY(pos.yPx) + pos.hPx;
  });
  const blockBottoms = expandableBlocks.map((b) => b.yPx + shiftForY(b.yPx) + b.naturalPx);
  const maxAbsoluteBottom = [...fieldBottoms, ...blockBottoms].reduce(
    (acc, n) => Math.max(acc, n),
    0
  );
  const requiredDocHeightPx = Math.max(
    A4_PAGE_HEIGHT_PX,
    maxAbsoluteBottom + DOC_BOTTOM_PADDING_PX
  );

  // Wrap in a minimal A4-ish HTML page — keeps the PDF rendering close
  // to the editor's visual width. The body uses `position: relative` so the
  // absolute overlay anchors correctly.
  //
  // @page is the BIG one for PDF alignment. Chrome's print mode (which our
  // PDF render runs under the hood) applies a default ~0.5" margin to every
  // page when no @page rule is present. Our 794px-wide
  // layout then gets squeezed into ~698px of usable width, which:
  //   - scales body text and shifts every absolutely-positioned field a
  //     few percent off its designed location, and
  //   - is the reason the realtor saw "preview is fine, sent doc is off"
  //     (the realtor preview iframe doesn't go through print mode at all).
  // Locking @page to A4 with zero margin makes the print-mode page exactly
  // the same geometry our editor / preview use, so absolute coords land
  // pixel-for-pixel where they should. The realtor's intentional document
  // padding still comes from `.webot-doc { padding: 48px 64px }`.
  const inner = body.innerHTML;
  // The document layout is FROZEN at 794px wide regardless of viewport size:
  // every field is `position: absolute` with px coords designed for that
  // width, and `max-width: 100%` would let the body shrink while leaving the
  // fields anchored — desyncing them from the text they were pinned to.
  //
  // Keep layout at its authored size and transform the completed document.
  // CSS zoom changes text layout in WebKit (including minimum-font sizing),
  // so it cannot be used for a coordinate-based document viewer.
  return `<!DOCTYPE html>
<html lang="he" dir="rtl">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <script>
    // FOUC guard — mark the document "shifts pending" BEFORE any of the
    // body parses. The stylesheet below hides .webot-doc while this marker
    // is set, and the shift pass at the end of the body removes it once
    // blocks/fields/body text sit at their final measured positions.
    // Without the guard, an overflowing listings table visibly overlaps
    // the body text for however long images take to load (the shift pass
    // used to wait for window.load), then everything jumps into place.
    // Set via JS rather than baked into the <html> tag so a consumer that
    // renders this HTML without scripts never hides the document.
    document.documentElement.setAttribute('data-webot-shifts-pending', '');
  </script>
  <style>
    /*
     * Arimo (Apache-licensed, metric-compatible with Arial) inlined as
     * base64 data URLs so the iframe and the PDF renderer get the
     * font WITHOUT any network access. Critical because:
     *   - srcDoc iframes have ambiguous base-URL behaviour across
     *     browsers — relative font paths can fail silently.
     *   - the PDF renderer may be network-sandboxed. With external
     *     fonts the PDF falls back to system Arial-or-whatever, breaking
     *     the editor-vs-PDF parity we rely on.
     *   - Mobile Android devices do NOT ship Arial. With a plain Arial
     *     fallback they land on Roboto, whose different glyph metrics
     *     shift paragraph heights and visibly drift the absolutely-
     *     positioned template fields.
     *
     * Two subsets (Latin + Hebrew), each a single variable font binary
     * covering the weights we use (400 / 600 / 700). The unicode-range
     * lets the browser pick the right subset per character automatically.
     * See src/services/arimoFontData.ts for source files and regen
     * instructions.
     */
    @font-face {
      font-family: 'Arimo';
      font-weight: 400;
      font-style: normal;
      src: url(data:font/woff2;base64,${ARIMO_LATIN_WOFF2_BASE64}) format('woff2');
      unicode-range: ${ARIMO_LATIN_UNICODE_RANGE};
    }
    @font-face {
      font-family: 'Arimo';
      font-weight: 600;
      font-style: normal;
      src: url(data:font/woff2;base64,${ARIMO_LATIN_WOFF2_BASE64}) format('woff2');
      unicode-range: ${ARIMO_LATIN_UNICODE_RANGE};
    }
    @font-face {
      font-family: 'Arimo';
      font-weight: 700;
      font-style: normal;
      src: url(data:font/woff2;base64,${ARIMO_LATIN_WOFF2_BASE64}) format('woff2');
      unicode-range: ${ARIMO_LATIN_UNICODE_RANGE};
    }
    @font-face {
      font-family: 'Arimo';
      font-weight: 400;
      font-style: normal;
      src: url(data:font/woff2;base64,${ARIMO_HEBREW_WOFF2_BASE64}) format('woff2');
      unicode-range: ${ARIMO_HEBREW_UNICODE_RANGE};
    }
    @font-face {
      font-family: 'Arimo';
      font-weight: 600;
      font-style: normal;
      src: url(data:font/woff2;base64,${ARIMO_HEBREW_WOFF2_BASE64}) format('woff2');
      unicode-range: ${ARIMO_HEBREW_UNICODE_RANGE};
    }
    @font-face {
      font-family: 'Arimo';
      font-weight: 700;
      font-style: normal;
      src: url(data:font/woff2;base64,${ARIMO_HEBREW_WOFF2_BASE64}) format('woff2');
      unicode-range: ${ARIMO_HEBREW_UNICODE_RANGE};
    }
    @page { size: A4; margin: 0; }
    /*
     * Disable mobile browsers' text auto-inflation. iOS Safari and
     * Android Chrome default to 'text-size-adjust: auto', which inflates
     * 14px body text by ~1.2-1.5x inside our 794px-wide doc when viewed
     * on a mobile-width screen. The paragraphs then render taller, but
     * absolutely-positioned fields stay at their authored pixel
     * coordinates — so on mobile the fields visibly drift relative to
     * the surrounding text. Locking the adjustment to 100% keeps the
     * iframe preview pixel-equivalent across desktop and mobile.
     */
    html { -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }
    /*
     * Pre-reveal hide. Listings blocks are absolute overlays — until the
     * shift pass pushes the body text down past an overflowing table, the
     * table (and its consent lines) visibly sits ON TOP of the template
     * text. Hiding the doc until the first shift pass has run makes that
     * overlap unobservable. visibility (not display) keeps layout live so
     * the pass can measure real heights while hidden. A small spinner
     * fills the wait; both toggle off when the shift pass removes the
     * data-webot-shifts-pending marker set by the head guard script.
     */
    html[data-webot-shifts-pending] .webot-doc { visibility: hidden; }
    .webot-loading { display: none; }
    html[data-webot-shifts-pending] .webot-loading {
      display: flex;
      position: fixed;
      inset: 0;
      align-items: center;
      justify-content: center;
    }
    .webot-loading > div {
      width: 28px;
      height: 28px;
      border: 3px solid #e5e7eb;
      border-top-color: #9ca3af;
      border-radius: 50%;
      animation: webot-spin 0.8s linear infinite;
    }
    @keyframes webot-spin { to { transform: rotate(360deg); } }
    body { font-family: ${SIGNATURE_DOCUMENT_FONT_FAMILY}; color: #1a1a1a; line-height: ${SIGNATURE_DOCUMENT_LINE_HEIGHT}; margin: 0; }
    /* Mirror src/theme/globalStyles.ts so absolute field coordinates land at
       the same screen position in the iframe as in the editor. The editor's
       host document gets a universal margin/padding reset and block images
       via MUI GlobalStyles; the iframe is a separate document that never
       receives them, so without this reproduction lists end up ~2px taller
       per UL/OL boundary and inline images below paragraphs add a ~22px line
       box per occurrence — both drift cumulatively against the absolutely-
       positioned fields anchored to designed editor coordinates. */
    * { margin: 0; padding: 0; box-sizing: border-box; }
    img { display: block; max-width: 100%; }
    /*
     * Layout-parity rules with the editor.
     *
     * ProseMirror (the editor's editable surface) ships with the rules
     * below baked into prosemirror-view's stylesheet. Without mirroring
     * them here, the same paragraph wraps at a different word in the
     * editor vs. the rendered/PDF output — and because every field is
     * absolutely positioned, that drift puts stamped values on the wrong
     * line of body text.
     *
     * The biggest offenders (verified by toggling each rule):
     *   - font-variant-ligatures: none + font-feature-settings: "liga" 0
     *     Disables ligatures ("fi", "fl", "ff", "ti" combined glyphs).
     *     With ligatures on (browser default), those pairs render a few
     *     pixels narrower than the un-ligatured glyphs the editor uses,
     *     which is enough to push borderline lines to wrap one word
     *     earlier or later.
     *   - word-wrap: break-word
     *     Matches ProseMirror's behaviour for very long unbreakable
     *     tokens (URLs, IDs).
     *   - white-space: break-spaces
     *     Replaces pre-wrap with the modern variant the editor uses,
     *     which allows trailing whitespace at a line edge to wrap rather
     *     than be discarded.
     *   - text-rendering: geometricPrecision
     *     Takes glyph advances straight from the font instead of letting
     *     the platform round them to whole pixels. THIS one is not about
     *     matching ProseMirror — it's about matching the realtor's own
     *     machine. The canonical renderer is Chromium on Debian (see the
     *     server's Dockerfile), where Chrome's default font render style
     *     has subpixel positioning OFF, so every glyph advance is rounded
     *     up or down to an integer; the realtor's Chrome on Windows/macOS
     *     uses the fractional advance. On ordinary prose the two agree on
     *     where to wrap. On a line that is nearly full they do not, and
     *     a document laid out with runs of spaces is exactly that:
     *     measured 2026-09-29 on template 2zozPr65iLCbQihWwpZ3, whose
     *     signature block is one paragraph reading
     *     "26 underscores, 62 spaces, 26 underscores, …". At Arimo's real
     *     advances (space 3.8897px, underscore 7.7862px) that run costs
     *     645.98px of the 666px content box and wraps as two columns; at
     *     whole-pixel advances (4 and 8) it costs 664px, the next space
     *     puts the first break opportunity past 668px, and the second
     *     underscore run drops to its own line — which is what the
     *     realtor saw in placement mode, and what their customers were
     *     receiving, while the editor showed neat columns.
     *
     * Applied at .webot-doc so every text descendant inherits the
     * font-feature and text-rendering properties; word-wrap / white-space
     * aren't inherited so they're repeated on the block-level text
     * elements below.
     */
    .webot-stage {
      position: relative;
      width: ${SIGNATURE_DOCUMENT_WIDTH_PX}px;
      margin: 0 auto;
      overflow: hidden;
    }
    .webot-doc {
      position: relative;
      width: ${SIGNATURE_DOCUMENT_WIDTH_PX}px;
      margin: 0;
      /* No padding here on purpose — the body-text padding lives on the
         inner .webot-body wrapper below. This keeps the containing block
         for absolutely-positioned fields and listings blocks aligned with
         the .webot-doc border box, mirroring the editor (where the field
         overlay sits inside the un-padded docBody Box while .ProseMirror
         carries the body padding). Without this split, the absolute
         positioning origin shifts by (64, 48) between editor and preview
         and every field renders that far off-target. */
      box-sizing: border-box;
      font-size: ${SIGNATURE_DOCUMENT_FONT_SIZE_PX}px;
      min-height: ${requiredDocHeightPx}px;
      -webkit-font-variant-ligatures: none;
      font-variant-ligatures: none;
      font-feature-settings: "liga" 0;
      text-rendering: geometricPrecision;
      transform-origin: top left;
    }
    /* Inner padding wrapper — receives the body text only. Mirrors the
       editor's .ProseMirror placement (padded child of an un-padded
       positioned wrapper) so absolute coordinates designed in the editor
       land at the same screen position when rendered. */
    .webot-body {
      padding: ${SIGNATURE_DOCUMENT_PADDING};
      box-sizing: border-box;
    }
    h1 { font-size: 24px; margin: 16px 0 8px; }
    h2 { font-size: 20px; margin: 16px 0 6px; }
    h3 { font-size: 17px; margin: 14px 0 4px; }
    p, h1, h2, h3, li {
      white-space: pre-wrap;
      white-space: break-spaces;
      word-wrap: break-word;
    }
    p { margin: 0 0 12px; }
    /* TipTap stores empty paragraphs as <p></p>. In the editor ProseMirror
       inserts a synthetic break that gives the line its full height; in
       plain HTML the empty <p> collapses to zero height and the realtor's
       blank lines disappear. A non-breaking space pseudo-element keeps
       each empty paragraph at the document's line height so the rendered
       layout matches the editor exactly. */
    p:empty::before, h1:empty::before, h2:empty::before, h3:empty::before { content: "\\00a0"; }
    /* Lists render with their NATIVE markup (same structure + spacing as the
       editor) but WITHOUT native markers: html2canvas draws ::marker with
       LTR-only logic, so the PDF raster would put bullets/numbers on the
       wrong side of an RTL document. Markers are injected instead as
       absolutely-positioned spans (data-list-marker, see injectListMarkers)
       sitting in this 24px padding band — they can never affect text flow. */
    ul, ol { padding-inline-start: 24px; list-style: none; }
    li { position: relative; }
    table { border-collapse: collapse; }
    /* Tables authored by the realtor (Word imports + manual TipTap tables)
       get a default cell border so they actually look like a table in the
       PDF. Listings-block tables set their borders inline when the
       "bordered" format is picked and we deliberately leave the borderless
       variant unstyled — hence the .tt-table scope here. */
    .tt-table { width: 100%; table-layout: fixed; margin: 8px 0; }
    .tt-table td, .tt-table th {
      border: 1px solid #d0d7de;
      padding: 6px 8px;
      vertical-align: top;
    }
    .tt-table th { background: #f6f8fa; font-weight: 600; text-align: start; }
    /* Borderless tables — set either by the Word importer (when the
       source .docx had <w:tblBorders> set to none) or via the editor's
       table toolbar. We zero the borders AND the header tint so the
       rendered PDF matches the editor's borderless view. */
    .tt-table[data-borderless="true"] td,
    .tt-table[data-borderless="true"] th {
      border: 0;
    }
    .tt-table[data-borderless="true"] th {
      background: transparent;
      font-weight: inherit;
    }
    /* Keep a single listings row from being split across two PDF pages —
       the row is the atomic unit of the table. The table itself is
       allowed to break between rows. */
    tr { break-inside: avoid; page-break-inside: avoid; }
    img { max-width: 100%; height: auto; }
    @media print {
      .webot-stage { width: ${SIGNATURE_DOCUMENT_WIDTH_PX}px !important; height: auto !important; overflow: visible; }
      .webot-doc { transform: none !important; }
    }
  </style>
</head>
<body><div class="webot-stage"><div class="webot-doc"><div class="webot-body">${inner}</div>${absoluteBlocksHtml}${termsBlockHtml}${absoluteFieldsHtml}</div></div><div class="webot-loading" aria-hidden="true"><div></div></div>
<script>
(function () {
  // Fit-to-width base factor: \`min(1, viewport/794)\` — the document is
  // designed at A4 width (794px); on narrower viewports (mobile preview
  // iframes) it scales DOWN to fill the screen edge-to-edge instead of
  // overflowing and getting clipped (in RTL the overflow clips off the
  // left edge, which read as "the preview is cut" on iPhone). Never
  // up-scales past 1× on wide viewports. The user zoom multiplier from
  // the parent dialog's +/− controls compounds on top (1× = fit), so
  // realtors can still zoom in for readability. The print-mode PDF
  // viewport is A4 (~794px) → base factor 1 there, so the rendered PDF
  // geometry is unaffected by the auto-fit.
  var userFactor = 1;
  // Authoritative viewport width (px) pushed by the parent dialog via a
  // 'webot:setViewport' message; 0 = unknown/not-sent. iOS Safari IGNORES a
  // viewport <meta> inside a subframe, so this iframe gets a wide (~794px+)
  // layout viewport and document.documentElement.clientWidth reports ~794
  // instead of the real ~390px device width — baseFitFactor() would then hit
  // the 794-24 snap and never downscale, so the RTL doc overflows and clips
  // off the left edge on iPhone. The PARENT DOM measures this iframe's real
  // rendered width reliably, so we prefer it whenever it has been sent. The
  // PDF pipeline never posts this message, so its 794 -> factor 1 path is
  // untouched.
  var parentViewportWidth = 0;
  function baseFitFactor() {
    var vw = parentViewportWidth || document.documentElement.clientWidth || window.innerWidth || 794;
    // Snap near-794 widths to exactly 1: the PDF pipeline renders this HTML
    // in a hidden 794px iframe, where a classic desktop scrollbar (~15px)
    // shaves clientWidth to ~779 — without the snap the whole PDF would
    // render ~2% smaller. 24px covers every scrollbar width in practice
    // while real mobile viewports (~390px) stay far below the cutoff.
    if (vw >= 794 - 24) return 1;
    return vw / 794;
  }
  function apply() {
    var doc = document.querySelector('.webot-doc');
    var stage = document.querySelector('.webot-stage');
    if (!doc || !stage) return;
    var factor = baseFitFactor() * userFactor;
    doc.style.transform = 'scale(' + factor + ')';
    stage.style.width = (794 * factor) + 'px';
    stage.style.height = (Math.max(doc.offsetHeight, doc.scrollHeight) * factor) + 'px';
  }
  // Refit when the iframe resizes (orientation change, dialog resize).
  window.addEventListener('resize', apply);
  // Parent dialog drives the user multiplier through postMessage so the
  // toolbar's +/− buttons can scale the doc when needed.
  window.addEventListener('message', function (e) {
    var d = e && e.data;
    if (d && d.type === 'webot:setZoom' && typeof d.factor === 'number' && isFinite(d.factor) && d.factor > 0) {
      userFactor = d.factor;
      apply();
    } else if (d && d.type === 'webot:setViewport' && typeof d.widthPx === 'number' && d.widthPx > 0) {
      // Parent-measured viewport width — authoritative on iOS where the
      // in-iframe clientWidth is wrong (see parentViewportWidth above).
      parentViewportWidth = d.widthPx;
      apply();
    }
  });
  apply();

  // Reflow absolutely-positioned blocks and fields based on REAL block
  // heights measured from the rendered DOM, plus push body-flow text
  // past overflowing tables. All shifts derive from the SAME measured
  // overflow per block, so fields, subsequent blocks, and body paragraphs
  // all move by exactly the same amount — no estimate, no static buffer
  // that lands asymmetrically.
  //
  // Logic (idempotent — runs on each trigger listed in the scheduling
  // section below, once measurements can be trusted):
  //   1. Sort blocks by designed Y ascending.
  //   2. For each block in order: set its \`top\` to (designed Y +
  //      overflow of blocks whose designed bottom is above it), force a layout flush by
  //      reading offsetHeight, record real overflow = real height -
  //      designed height. Accumulate.
  //   3. For each field: compute shift = sum of measured overflows of
  //      blocks whose designed bottom is <= field's designed Y. Apply
  //      to top.
  //   4. For each overflowing block: find the first body paragraph whose
  //      ORIGINAL offsetTop is past the designed bottom and add that
  //      block's overflow to its padding. All comparisons use design space.
  //
  // The print-mode PDF render runs JS before snapshotting, so the
  // final signed document picks these positions up too.
  function applyShiftsFromMeasurements() {
    var doc = document.querySelector('.webot-doc');
    if (!doc) return;

    // The pass runs more than once (early reveal pass + the window.load
    // pass that the PDF pipeline's "shifts are final after load" contract
    // relies on — see the scheduling below). Blocks and fields recompute
    // their positions from the data-*-designed attributes, so they're
    // naturally idempotent, but body pushes ACCUMULATE paddingTop — clear
    // the previous run's pushes so a re-run derives them from scratch
    // instead of doubling.
    var prevPushed = doc.querySelectorAll('[data-body-pushed]');
    for (var pri = 0; pri < prevPushed.length; pri++) {
      prevPushed[pri].style.paddingTop = '';
      prevPushed[pri].removeAttribute('data-body-pushed');
    }

    var blockEls = [];
    var blockNodes = doc.querySelectorAll('[data-block-y-designed]');
    for (var i = 0; i < blockNodes.length; i++) blockEls.push(blockNodes[i]);
    blockEls.sort(function (a, b) {
      return (
        parseFloat(a.getAttribute('data-block-y-designed') || '0') -
        parseFloat(b.getAttribute('data-block-y-designed') || '0')
      );
    });

    var blockData = [];
    for (var bi = 0; bi < blockEls.length; bi++) {
      var blk = blockEls[bi];
      var yDes = parseFloat(blk.getAttribute('data-block-y-designed') || '0');
      var hDes = parseFloat(blk.getAttribute('data-block-h-designed') || '0');
      var blockShift = 0;
      for (var earlier = 0; earlier < blockData.length; earlier++) {
        if (blockData[earlier].designedBottom <= yDes) blockShift += blockData[earlier].realOverflow;
      }
      blk.style.top = (yDes + blockShift) + 'px';
      // offsetHeight forces layout — value reflects min-height + real
      // content (whichever is larger) AFTER the new top is applied.
      var realHeight = blk.offsetHeight;
      var realOverflow = Math.max(0, realHeight - hDes);
      blockData.push({
        designedBottom: yDes + hDes,
        realOverflow: realOverflow
      });
    }

    var fieldEls = doc.querySelectorAll('[data-field-y-designed]');
    for (var fi = 0; fi < fieldEls.length; fi++) {
      var fld = fieldEls[fi];
      var fy = parseFloat(fld.getAttribute('data-field-y-designed') || '0');
      var fShift = 0;
      for (var fbi = 0; fbi < blockData.length; fbi++) {
        if (blockData[fbi].designedBottom <= fy) {
          fShift += blockData[fbi].realOverflow;
        }
      }
      fld.style.top = (fy + fShift) + 'px';
    }

    var body = doc.querySelector('.webot-body');
    if (body && blockData.length) {
      var paragraphs = [];
      for (var ci = 0; ci < body.children.length; ci++) {
        paragraphs.push({ element: body.children[ci], originalTop: body.children[ci].offsetTop });
      }
      // All thresholds use the unshifted geometry, just like fields.
      // Looking up a later block against already-shifted paragraphs can
      // push an earlier paragraph again and separate fields from its text.
      var pushBlocks = [];
      for (var pi = 0; pi < blockData.length; pi++) {
        if (blockData[pi].realOverflow > 0) pushBlocks.push(blockData[pi]);
      }
      pushBlocks.sort(function (a, b) { return a.designedBottom - b.designedBottom; });

      for (var pbi = 0; pbi < pushBlocks.length; pbi++) {
        var pb = pushBlocks[pbi];
        for (var pi2 = 0; pi2 < paragraphs.length; pi2++) {
          var p = paragraphs[pi2].element;
          // \`>=\` to match the field-shift inclusion rule (field shifts
          // when its yPx is \`>=\` the block's designed bottom).
          if (paragraphs[pi2].originalTop >= pb.designedBottom) {
            // Use padding-top, NOT margin-top: adjacent block-level
            // siblings have their vertical margins collapsed (the larger
            // wins). Default \`<p>\` has margin-bottom: 12px, so setting
            // margin-top = realOverflow on this paragraph collapses
            // with the previous paragraph's 12px and the actual shift
            // becomes (realOverflow - 12). Fields shift by realOverflow
            // exactly, so the body-vs-fields delta becomes a static
            // 12px regardless of how big the table grows — exactly the
            // "fields go a few extra pixels lower than text" symptom.
            // Padding doesn't participate in margin collapse, so
            // padding-top = realOverflow shifts the paragraph's content
            // by exactly realOverflow.
            var existingPt = parseFloat(p.style.paddingTop || '0') || 0;
            p.style.paddingTop = (existingPt + pb.realOverflow) + 'px';
            p.setAttribute('data-body-pushed', 'true');
            break;
          }
        }
      }
    }
  }
  // ===== Scheduling + reveal =====
  // The document starts hidden (html[data-webot-shifts-pending], set by
  // the head guard script) so the overlap between absolute listings
  // blocks and not-yet-pushed body text is never visible. The first
  // completed shift pass reveals the document; the pass is idempotent so
  // it can safely run on BOTH triggers:
  //
  //   1. Early pass — as soon as measurements can be trusted: fonts
  //      ready (Arimo is inline base64, effectively instant) and every
  //      FLOW image settled. Field images (company logo / realtor
  //      signature — the slow remote ones that used to delay the shift
  //      by seconds) are absolutely positioned, so they can't move flow
  //      measurements and are deliberately NOT waited on. A hard cap
  //      keeps a hung image from blanking the document forever.
  //   2. window.load — unchanged from the original design. This is the
  //      contract the canonical compiler relies on (it waits for assets,
  //      reruns this pass, then freezes and measures the layout). It re-corrects
  //      the layout in the rare case a flow resource landed between the
  //      early pass and load.
  //
  // try/finally so a measurement error can never leave the doc hidden.
  function runShiftPass() {
    try {
      applyShiftsFromMeasurements();
      apply();
    } finally {
      document.documentElement.removeAttribute('data-webot-shifts-pending');
    }
  }

  function whenMeasurable(cb) {
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      cb();
    }
    // Cap: a broken or hanging flow image delays the reveal by at most
    // this. The load-time pass re-corrects afterwards if needed.
    var cap = setTimeout(finish, 3000);
    var waits = [];
    // Force style + layout once so the @font-face loads actually start —
    // document.fonts.ready can resolve early if nothing has triggered
    // the font request yet.
    void document.body.offsetHeight;
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      waits.push(document.fonts.ready);
    }
    var imgs = document.querySelectorAll('img');
    for (var wi = 0; wi < imgs.length; wi++) {
      (function (img) {
        // Absolute field images (logo / signature) can't affect flow
        // measurements — skip them so a slow logo doesn't delay reveal.
        if (img.closest && img.closest('[data-field-y-designed]')) return;
        if (img.complete) return;
        waits.push(new Promise(function (resolve) {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        }));
      })(imgs[wi]);
    }
    Promise.all(waits).then(
      function () { clearTimeout(cap); finish(); },
      function () { clearTimeout(cap); finish(); }
    );
  }

  whenMeasurable(runShiftPass);
  if (document.readyState === 'complete') runShiftPass();
  else window.addEventListener('load', runShiftPass);
  if (typeof ResizeObserver !== 'undefined') {
    var viewportObserver = new ResizeObserver(apply);
    viewportObserver.observe(document.querySelector('.webot-doc'));
  }

  // Ctrl/⌘ + mouse drag pans the document. Useful once the user zooms in
  // past fit-to-width and the document overflows the iframe. On Mac, ⌘ is
  // the natural modifier (Ctrl+click is a context menu); on Windows/Linux,
  // Ctrl works. Mobile single-finger scroll works natively because the
  // iframe's html element scrolls when content overflows, so no extra
  // touch handling is needed.
  var dragging = false;
  var lastX = 0;
  var lastY = 0;
  function setCursor(c) { document.body.style.cursor = c; }
  document.addEventListener('mousedown', function (e) {
    if (e.button !== 0) return; // left button only
    if (!(e.ctrlKey || e.metaKey)) return;
    dragging = true;
    lastX = e.clientX;
    lastY = e.clientY;
    setCursor('grabbing');
    e.preventDefault();
  });
  document.addEventListener('mousemove', function (e) {
    if (!dragging) return;
    var dx = e.clientX - lastX;
    var dy = e.clientY - lastY;
    lastX = e.clientX;
    lastY = e.clientY;
    // Negate so dragging the content right scrolls the viewport left,
    // matching the natural "grab and pull" feel users expect.
    window.scrollBy(-dx, -dy);
    e.preventDefault();
  });
  function endDrag() {
    if (!dragging) return;
    dragging = false;
    setCursor('');
  }
  document.addEventListener('mouseup', endDrag);
  document.addEventListener('mouseleave', endDrag);
  // Cursor cue: while the modifier is held (without dragging), show a grab
  // cursor so the user discovers the gesture without needing a tooltip.
  document.addEventListener('keydown', function (e) {
    if ((e.ctrlKey || e.metaKey) && !dragging) setCursor('grab');
  });
  document.addEventListener('keyup', function (e) {
    if (!dragging && !e.ctrlKey && !e.metaKey) setCursor('');
  });
})();
</script>
</body>
</html>`;
};

// ============================================================================
// Addresses addendum (hidden-addresses sends)
// ============================================================================

/**
 * Build the "addresses addendum" — the generic second document a signer sees
 * AFTER signing a hidden-addresses main document. Content: the document's
 * customer-facing name, a centered "כתובות נכסים" title, the original
 * document's number, a fixed confirmation sentence, the listings rendered as
 * a table (same shape as the main document's listings table, but with the
 * FULL addresses), and a signature field.
 *
 * The output is a self-contained HTML page in the same shell as
 * `renderHtmlForSend` (same inlined Arimo fonts, same `.webot-doc` /
 * `.webot-body` geometry) so it runs through the exact same
 * `renderHtmlToPdfWithFields` rasterization + field-extraction pipeline. The
 * signature field tag mimics the main renderer's contract: an absolutely-
 * stretched tag inside a positioned, explicitly-sized wrapper — that's the
 * rect extraction measures and the signing canvas overlays.
 */
export const ADDENDUM_SIGNATURE_FIELD_NAME = "addendum_signature";

/** Usable body width of the addendum page: 794px A4 minus .webot-body's 64px side padding. */
const ADDENDUM_TABLE_WIDTH_PX = 794 - 64 * 2;

export const renderAddressesAddendumHtml = (options: {
  /** Customer-facing name of the ORIGINAL document (template's friendly name). */
  documentTitle: string;
  /** The ORIGINAL document's formatted number (e.g. "RA-42"). */
  documentFormattedNumber: string;
  /**
   * Listing contexts with `address` set to the FULL (unredacted) address —
   * the caller maps `fullAddress` into `address` before passing them in.
   */
  listings: ListingContext[];
  /**
   * The main document's listings block — the addendum table mirrors its
   * columns / borders / font so both documents read as one set. Optional:
   * absent falls back to a bordered table with the default columns.
   */
  block?: ListingsBlock | null;
  /** Signer role name — must match the role on the document's signer. */
  signerRoleName: string;
}): string => {
  const { documentTitle, documentFormattedNumber, listings, block, signerRoleName } = options;

  // Mirror the main table's shape, but always as a TABLE (a bulleted-list
  // main block still discloses as a bordered table — the addendum's job is
  // an unambiguous address record) and stretched to the page's body width.
  // Per-column width overrides are intentionally dropped: they were tuned
  // for the block's designed width, not the full page.
  const tableBlock: ListingsBlock = {
    id: "addendum-listings",
    format: block && block.format !== "bulleted-list" ? block.format : "table-bordered",
    columns: block?.columns?.length ? block.columns : ["address", "price", "rooms", "square_meter"],
    position: { xPx: 0, yPx: 0, wPx: ADDENDUM_TABLE_WIDTH_PX, hPx: 40 },
    fontSize: block?.fontSize,
    color: block?.color
  };
  const tableHtml = renderListingsBlockInnerHtml(tableBlock, { listings, hideAddresses: false });

  const signatureTag =
    `<signature-field name="${escapeAttr(ADDENDUM_SIGNATURE_FIELD_NAME)}" ` +
    `role="${escapeAttr(signerRoleName)}" title="חתימה" align="right" ` +
    `style="display:inline-block;position:absolute;left:0;top:0;right:0;bottom:0;box-sizing:border-box;"></signature-field>`;

  return `<!DOCTYPE html>
<html lang="he" dir="rtl">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    /* Same inlined Arimo subsets as the main renderer — see renderHtmlForSend. */
    @font-face {
      font-family: 'Arimo';
      font-weight: 400;
      font-style: normal;
      src: url(data:font/woff2;base64,${ARIMO_LATIN_WOFF2_BASE64}) format('woff2');
      unicode-range: ${ARIMO_LATIN_UNICODE_RANGE};
    }
    @font-face {
      font-family: 'Arimo';
      font-weight: 600;
      font-style: normal;
      src: url(data:font/woff2;base64,${ARIMO_LATIN_WOFF2_BASE64}) format('woff2');
      unicode-range: ${ARIMO_LATIN_UNICODE_RANGE};
    }
    @font-face {
      font-family: 'Arimo';
      font-weight: 700;
      font-style: normal;
      src: url(data:font/woff2;base64,${ARIMO_LATIN_WOFF2_BASE64}) format('woff2');
      unicode-range: ${ARIMO_LATIN_UNICODE_RANGE};
    }
    @font-face {
      font-family: 'Arimo';
      font-weight: 400;
      font-style: normal;
      src: url(data:font/woff2;base64,${ARIMO_HEBREW_WOFF2_BASE64}) format('woff2');
      unicode-range: ${ARIMO_HEBREW_UNICODE_RANGE};
    }
    @font-face {
      font-family: 'Arimo';
      font-weight: 600;
      font-style: normal;
      src: url(data:font/woff2;base64,${ARIMO_HEBREW_WOFF2_BASE64}) format('woff2');
      unicode-range: ${ARIMO_HEBREW_UNICODE_RANGE};
    }
    @font-face {
      font-family: 'Arimo';
      font-weight: 700;
      font-style: normal;
      src: url(data:font/woff2;base64,${ARIMO_HEBREW_WOFF2_BASE64}) format('woff2');
      unicode-range: ${ARIMO_HEBREW_UNICODE_RANGE};
    }
    @page { size: A4; margin: 0; }
    html { -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }
    body { font-family: "Arimo", Arial, Helvetica, sans-serif; color: #1a1a1a; line-height: 1.6; margin: 0; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    .webot-doc {
      position: relative;
      width: 794px;
      margin: 0 auto;
      box-sizing: border-box;
      font-size: 14px;
      min-height: 1123px;
      -webkit-font-variant-ligatures: none;
      font-variant-ligatures: none;
      font-feature-settings: "liga" 0;
      /* Same platform-independent advances as the main document — see the
         "Layout-parity rules with the editor" block above. This addendum is
         built from our own markup so it has no near-full lines today, but it
         goes through the same renderer and its signature field is measured
         out of the result. */
      text-rendering: geometricPrecision;
    }
    .webot-body { padding: 48px 64px; box-sizing: border-box; }
    p, li { white-space: pre-wrap; word-wrap: break-word; }
    tr { break-inside: avoid; page-break-inside: avoid; }
  </style>
</head>
<body><div class="webot-doc"><div class="webot-body">
  <div style="text-align:center;font-size:16px;font-weight:600;color:#374151;margin:16px 0 4px;">${escapeHtml(
    documentTitle
  )}</div>
  <h1 style="text-align:center;font-size:26px;font-weight:700;margin:0 0 8px;">כתובות נכסים</h1>
  <div style="text-align:center;font-size:14px;color:#555;margin-bottom:32px;">${escapeHtml(
    documentFormattedNumber
  )}</div>
  <p style="font-size:15px;margin-bottom:20px;">אנו מאשרים שקבלנו את פרטי הנכס(ים) ומוסכם שפרטים אלו הינם חלק מהזמנת שירותי התיווך שחתמנו על תנאיה.</p>
  <div style="margin-bottom:48px;">${tableHtml}</div>
  <div style="margin-top:24px;">
    <div style="font-size:14px;margin-bottom:6px;">חתימה:</div>
    <div style="position:relative;width:220px;height:90px;border-bottom:1px solid #9ca3af;">${signatureTag}</div>
  </div>
</div></div>
</body>
</html>`;
};
